import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { supabase, writeTimeoutSignal } from '../../lib/supabase';
import { fetchAllTasks } from '../../lib/supabaseTasks';
import { pushUndo } from '../../lib/undoStack';
import { collapseOverdueRecurring } from '../../lib/recurringTasks';
import { Switch } from '../ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CountUp } from '@/components/ui/count-up';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { formatTimeAgo, formatTime12h } from '../../utils/timeFormat';
import { stripHtml } from '../../utils/html';
import { spaNavigate } from '../../lib/spaNavigate';
import StudioDashboardView from '../studio/dashboard/StudioDashboardView';
import { resolveAreaLabel } from '../onboarding/utils/constants';
import EmailFlowsManager from '../admin/EmailFlowsManager';
import KnowledgeBaseManager from '../admin/KnowledgeBaseManager';
import AnnouncementsManager from '../admin/AnnouncementsManager';
import WorkshopsManager from '../admin/WorkshopsManager';
import HealthDashboard from '../admin/HealthDashboard';
import PlanningCenterWidget from './PlanningCenterWidget';
import ServicesWidget from './ServicesWidget';
import { useServicesMode } from '../services/hooks/useServicesMode';
import { servicesAdminFor } from '../services/hooks/useServicesMode';   // Fix 7 G4
import { useServicesConnectGuard } from '../services/components/ServicesConnectGuard';
import { TeamBonusCallout } from './TeamBonusCallout';
import { UserAvatar } from '@/components/shared';
import DownloadAppContent from '@/components/shared/DownloadAppContent';
import { useAvatarCache } from '@/contexts';
import { NotificationPanel } from '../shared/NotificationPanel';
import { ThemeMenuItem } from '../shared/ThemeToggle';
import { DesignMenuItem } from '../shared/DesignToggle';
import { useDesign } from '../../contexts/DesignContext';
import { AddTaskDialog, EditTaskDialog, INITIAL_TASK_STATE, createPlannerTask, recurrencePayloadFields } from '../planner/views';
import { useTaskEdit } from '../planner/hooks/useTaskEdit';
import { useSharedItems, visibleSharedTasks } from '../planner/hooks/useSharedItems';
import { usePlannerSubtasks } from '../planner/hooks/usePlannerSubtasks';
import SharedTasksSection from '@/components/shared/SharedTasksSection';
import SharedTaskEditDialog from '@/components/shared/SharedTaskEditDialog';
import { SubtaskProgressChip, SubtaskChecklist } from '@/components/shared/Subtasks';
import PriorityCheckbox, { comparePriorityTasks } from '@/components/shared/PriorityCheckbox';
import { 
  Calendar as CalendarIcon, 
  MessageSquare, 
  Users,
  Heart, 
  Bot, 
  LogOut, 
  Plus, 
  LayoutDashboard,
  Music,
  Bell,
  Send,
  QrCode,
  Copy,
  Download,
  BookOpen,
  User,
  CreditCard,
  Settings,
  HelpCircle,
  Keyboard,
  ChevronDown,
  Mic,
  UserCheck,
  Trash2,
  Lightbulb,
  Bug,
  Workflow,
  Globe,
  ExternalLink,
  Mail,
  UserPlus,
  BarChart3,
  Link2,
  Settings2,
  Smartphone,
  Megaphone,
  GraduationCap,
  Play,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Super admin email - only this user sees email flows menu
const SUPER_ADMIN_EMAILS = ['alex@worshipministrytraining.com'];

/**
 * StudioOrPlainModal — one set of children, two shells.
 * Studio renders the Dialog primitive (scrim, Esc, focus trap, the one shadow);
 * classic renders the hand-rolled overlay it always had, class for class.
 */
function StudioOrPlainModal({ isStudio, onClose, className, children }) {
  if (isStudio) {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className={className}>{children}</DialogContent>
      </Dialog>
    );
  }


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div data-ui="float" className="bg-card rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Dashboard({ user, onSignOut, onOpenApp, onOpenSettings, currentPlan = 'free', setHoveredItem, globalTasks = [], setGlobalTasks, globalRecentComms = [], archivedConversationIds = [], notificationHook, markMessagesAsRead, pendingOnboardingNotifsRef, setShowQuickCompose, setPcoServicePlan, setShowQuickAdd }) {
  const { getCachedAvatar } = useAvatarCache();
  // Filter today's tasks from global state (including overdue incomplete tasks)
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // Get tasks for today
  let tasksForToday = globalTasks.filter(t => {
    // Handle both 'yyyy-MM-dd' format and ISO date strings
    const taskDate = t.date ? t.date.substring(0, 10) : null;
    return taskDate === today;
  });
  
  // Also include overdue incomplete tasks (auto-forward like Planner does).
  // Recurring series collapse to ONE overdue entry (latest missed occurrence).
  const overdueTasks = collapseOverdueRecurring(globalTasks.filter(t => {
    const taskDate = t.date ? t.date.substring(0, 10) : null;
    return taskDate && taskDate < today && !t.completed;
  })).map(t => ({ ...t, isOverdue: true }));
  
  // Combine: overdue first, then by priority (high→low), then time
  const todayTasks = [...overdueTasks, ...tasksForToday].sort(comparePriorityTasks);
  
  // Use global recent comms - filter out archived conversations
  const recentComms = useMemo(() => {
    return globalRecentComms.filter(item => {
      // Messages have conversationId - filter out if archived
      if (item.conversationId && archivedConversationIds.includes(item.conversationId)) {
        return false;
      }
      return true;
    });
  }, [globalRecentComms, archivedConversationIds]);
  
  // Use notification hook — single source of truth
  const { notifications: teamCommsNotifications, unreadCount: commsUnreadCount, markRead, markReadByIds, markAllRead, markReadByTarget } = notificationHook || {};
  
  // Track task that was just completed for animation
  const [completingTaskId, setCompletingTaskId] = useState(null);
  
  const [aiMessage, setAiMessage] = useState('');
  // §45 (owner 2026-09-24): the Studio Worship AI widget lists the three most
  // recent conversations above its chips and ask field. Studio-only state; the
  // fetch below runs only when `isStudio`, so classic's request set is unchanged.
  const [aiConversations, setAiConversations] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState(INITIAL_TASK_STATE);
  const [verseOfTheDay, setVerseOfTheDay] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showEmailFlows, setShowEmailFlows] = useState(false);
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showWorkshops, setShowWorkshops] = useState(false);
  const [featuredWorkshop, setFeaturedWorkshop] = useState(null);
  const [showHealth, setShowHealth] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);

  // Seed planner review notifications (quarterly/annual wins) — fire & forget.
  // The overview endpoint creates the bell notification when a review window opens.
  // ADMIN-ONLY: the planner (and therefore reviews) is only available to admins/owners.
  useEffect(() => {
    if (!user?.id) return;
    if (user?.role !== 'admin' && user?.role !== 'owner') return;
    fetch(`${API_URL}/api/planner/reviews/overview/${user.id}`).catch(() => {});
  }, [user?.id, user?.role]);

  // Featured quarterly workshop for the team-building widget strip.
  useEffect(() => {
    if (!user?.id || !['owner', 'admin'].includes(user?.role)) return; // R12: admins only
    fetch(`${API_URL}/api/training/featured-workshop?user_id=${encodeURIComponent(user.id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && !d.coming_soon) setFeaturedWorkshop(d); })
      .catch(() => {});
  }, [user?.id, user?.role]);
  // showQuickCompose + PCO context state are lifted to App.js so the global
  // Shift+M shortcut can open the modal from any route. Setters come in via
  // props.
  const [onboardingNotifications, setOnboardingNotifications] = useState([]);
  const [superAdminNotifications, setSuperAdminNotifications] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [hasTeamMembers, setHasTeamMembers] = useState(true);
  const [onboardingCandidates, setOnboardingCandidates] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [trackBreakdown, setTrackBreakdown] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(globalTasks.length === 0);

  // Shared tasks (church-level, Mongo-backed)
  const { sharedItems, sharedContext, createSharedItem, toggleSharedItem, updateSharedItem, deleteSharedItem } = useSharedItems(user?.id, { focusRefetch: true });
  const [editingSharedTask, setEditingSharedTask] = useState(null);
  const [highlightSharedId, setHighlightSharedId] = useState(null);
  const mySharedTasks = useMemo(() => visibleSharedTasks(sharedItems), [sharedItems]);

  // Sub-tasks (template per series, checkmarks per instance)
  const subtasksApi = usePlannerSubtasks(user?.id);
  // Declared after useSharedItems/usePlannerSubtasks: the edit hook needs both for assign-after-create (R34).
  const { showEditTask, setShowEditTask, editingTaskData, setEditingTaskData, handleEditTask, updateTask } = useTaskEdit({ tasks: globalTasks, setTasks: setGlobalTasks, userId: user?.id, createSharedItem, deleteSharedItem, subtasksApi });
  const [expandedSubtasks, setExpandedSubtasks] = useState(() => new Set());
  const toggleSubtaskExpand = (taskId) => {
    setExpandedSubtasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  const [churchTimezone, setChurchTimezone] = useState(null);
  const [showDevoSettings, setShowDevoSettings] = useState(false);
  const [showDownloadApp, setShowDownloadApp] = useState(false);
  const [devoSettings, setDevoSettings] = useState({ post_day: 'monday', group_ids: [], enabled: true, current_week: null });
  const [churchGroups, setChurchGroups] = useState([]);
  const [devoSettingsLoading, setDevoSettingsLoading] = useState(false);
  const [devoSettingsFetched, setDevoSettingsFetched] = useState(false);

  // Fetch devo settings once (deferred) so the widget's auto-post switch is live.
  useEffect(() => {
    if (!user?.church_id) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/devotions/settings?church_id=${user.church_id}`);
        if (res.ok) {
          setDevoSettings(await res.json());
          setDevoSettingsFetched(true);
        }
      } catch (err) { console.error('Error fetching devo settings:', err); }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.church_id]);

  // Inline widget switch: optimistic toggle + full-settings save (PUT replaces).
  // Re-fetches fresh settings first so a stale tab can't regress current_week
  // (the weekly cron advances it server-side).
  const toggleDevoAutoPost = async (checked) => {
    const prev = devoSettings;
    setDevoSettings(s => ({ ...s, enabled: checked }));
    try {
      let fresh = prev;
      try {
        const fres = await fetch(`${API_URL}/api/devotions/settings?church_id=${user.church_id}`);
        if (fres.ok) fresh = await fres.json();
      } catch { /* fall back to last-known settings */ }
      const res = await fetch(`${API_URL}/api/devotions/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          church_id: user.church_id,
          user_id: user.id,
          post_day: fresh.post_day,
          group_ids: fresh.group_ids,
          enabled: checked,
          current_week: fresh.current_week
        })
      });
      if (!res.ok) throw new Error(`save failed (${res.status})`);
      setDevoSettings({ ...fresh, enabled: checked });
    } catch (err) {
      console.error('Error toggling devo auto-post:', err);
      setDevoSettings(s => ({ ...s, enabled: prev.enabled }));
    }
  };
  const [planningCenterConnected, setPlanningCenterConnected] = useState(() => {
    try {
      return localStorage.getItem(`pc_connected_${user?.id}`) === 'true';
    } catch { return false; }
  });
  const [planningCenterLoading, setPlanningCenterLoading] = useState(() => {
    try {
      return localStorage.getItem(`pc_connected_${user?.id}`) === null;
    } catch { return true; }
  });
  const { mode: servicesMode, loading: servicesModeLoading } = useServicesMode(user?.id);
  // Services (Phase 5): warn before a PCO connect hides Simple Planner services (M-01).
  const servicesConnectGuard = useServicesConnectGuard(user?.id);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  // Track if component has mounted - defer non-critical fetches
  const [hasMounted, setHasMounted] = useState(false);

  // Defer non-critical fetches until after initial render
  useEffect(() => {
    // Use requestIdleCallback or setTimeout to defer
    const deferFetch = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
    const handle = deferFetch(() => setHasMounted(true));
    return () => {
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);
  
  // Check if user is super admin
  const isSuperAdmin = SUPER_ADMIN_EMAILS.some(e => e.toLowerCase() === user?.email?.toLowerCase());

  // Super-admin platform alerts (team-bonus granted/failed) — surfaced in the
  // main notification bell (no separate bell). Polling-based; events are low-volume.
  useEffect(() => {
    if (!isSuperAdmin || !user?.email) return;
    const fetchSuperAdminNotifs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin-churches/notifications?user_email=${encodeURIComponent(user.email)}`);
        if (!res.ok) return;
        const data = await res.json();
        // No "cancelled" guard on purpose: a guard here raced with the post-login
        // auth-bootstrap re-renders (effect teardown flipped it before the only
        // in-flight fetch resolved, dropping the result). The response is the
        // canonical server state, the setter is idempotent, and render already
        // gates display by isSuperAdmin — so an unconditional set is safe.
        setSuperAdminNotifications(data.notifications || []);
      } catch { /* transient — next poll retries */ }
    };
    fetchSuperAdminNotifs();
    const timer = setInterval(fetchSuperAdminNotifs, 60000);
    return () => clearInterval(timer);
  }, [isSuperAdmin, user?.email]);

  // One lightweight health-status fetch on load (super-admin only) to drive the
  // menu badge. No polling — the full dashboard handles live refresh when open.
  useEffect(() => {
    if (!isSuperAdmin || !user?.email) return;
    fetch(`${API_URL}/api/admin/health?user_email=${encodeURIComponent(user.email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.status) setHealthStatus(d.status); })
      .catch(() => {});
  }, [isSuperAdmin, user?.email]);

  // Combined unread count for the single notification bell
  const bellUnread = (commsUnreadCount || 0)
    + onboardingNotifications.filter(n => !n.read).length
    + superAdminNotifications.filter(n => !n.is_read).length;

  // Bell nudge: one subtle ring when unread count increases (not on first load)
  const [bellRinging, setBellRinging] = useState(false);
  const prevBellUnread = useRef(null);
  useEffect(() => {
    const prev = prevBellUnread.current;
    prevBellUnread.current = bellUnread;
    if (prev !== null && bellUnread > prev) {
      setBellRinging(true);
      const t = setTimeout(() => setBellRinging(false), 900);
      return () => clearTimeout(t);
    }
  }, [bellUnread]);
  
  // Entrance animations play once; after they finish, freeze them so async
  // data swapping a grid child can never replay a card's rise-in (jerk).
  const [gridSettled, setGridSettled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGridSettled(true), 1100);
    return () => clearTimeout(t);
  }, []);

  // Check if user is admin or owner (for Planning Center feature)
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const { isStudio } = useDesign();
  // Studio header: greeting by time of day + a church · date sub-line (101 §7).
  const firstName = (user?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Leader').split(' ')[0];
  const studioGreeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();
  const studioDate = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // §45 (owner 2026-09-24): "the dashboard recent AI chats should be above".
  // The same call the Worship AI page makes on mount (WorshipAIChat.js:424) —
  // an existing endpoint, read once, no route added. Gated on `isStudio`: on
  // classic `/` this request is never issued (network parity).
  useEffect(() => {
    if (!isStudio || !user?.id) return undefined;
    let alive = true;
    fetch(`${API_URL}/api/ai/conversations/${user.id}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setAiConversations((d?.conversations || []).slice(0, 3)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isStudio, user?.id]);
  
  // Check Planning Center connection status
  useEffect(() => {
    // Check for PC connection callback in URL FIRST
    const urlParams = new URLSearchParams(window.location.search);
    const justConnected = urlParams.get('pc_connected') === 'true';
    
    if (justConnected) {
      // We just came from a successful OAuth — trust the URL param immediately
      setPlanningCenterConnected(true);
      setPlanningCenterLoading(false);
      try { localStorage.setItem(`pc_connected_${user?.id}`, 'true'); } catch { /* ignore */ }
      window.history.replaceState({}, document.title, window.location.pathname);
      return; // Skip async status check — widget will validate when it fetches schedule
    }
    
    if (urlParams.get('pc_error')) {
      console.error('Planning Center error:', urlParams.get('pc_error'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Normal flow: check status from API
    const checkPCConnection = async () => {
      if (!user?.id) return;
      try {
        const response = await fetch(`${API_URL}/api/planning-center/status/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setPlanningCenterConnected(data.connected);
          try { localStorage.setItem(`pc_connected_${user.id}`, String(data.connected)); } catch { /* ignore */ }
        }
      } catch (err) {
        console.error('Error checking PC connection:', err);
      } finally {
        setPlanningCenterLoading(false);
      }
    };
    checkPCConnection();
  }, [user?.id]);
  
  // Connect to Planning Center
  const connectPlanningCenter = async () => {
    if (await servicesConnectGuard.intercept(connectPlanningCenter)) return;
    try {
      const response = await fetch(`${API_URL}/api/planning-center/auth/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        // Redirect to Planning Center authorization
        window.location.href = data.auth_url;
      }
    } catch (err) {
      console.error('Error connecting to PC:', err);
    }
  };

  // Quick Links state - initialize from localStorage cache (user-specific, not church-wide)
  const [quickLinks, setQuickLinks] = useState(() => {
    try {
      const cached = localStorage.getItem(`quickLinks_${user?.id}`);
      return cached ? JSON.parse(cached) : [
        { id: '1', title: 'WMT Academy', url: 'https://members.worshipministrytraining.com/login?redirectTo=%2Ft%2FCommunity%2520Conversations&login=1' },
        { id: '2', title: 'Planning Center', url: 'https://services.planningcenteronline.com/schedule' }
      ];
    } catch {
      return [
        { id: '1', title: 'WMT Academy', url: 'https://members.worshipministrytraining.com/login?redirectTo=%2Ft%2FCommunity%2520Conversations&login=1' },
        { id: '2', title: 'Planning Center', url: 'https://services.planningcenteronline.com/schedule' }
      ];
    }
  });
  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  // Fetch quick links from backend (DEFERRED - only after mount) - now user-specific
  useEffect(() => {
    if (!hasMounted) return; // Defer until after initial render
    const fetchQuickLinks = async () => {
      if (!user?.id) return;
      try {
        const response = await fetch(`${API_URL}/api/auth/quick-links/user/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data.links && data.links.length > 0) {
            setQuickLinks(data.links);
            // Cache for instant load next time (user-specific cache)
            localStorage.setItem(`quickLinks_${user.id}`, JSON.stringify(data.links));
          }
        }
      } catch (err) {
        console.error('Error fetching quick links:', err);
      }
    };
    fetchQuickLinks();
  }, [hasMounted, user?.id]);

  // Save quick links to backend - now user-specific
  const saveQuickLinks = async (links) => {
    if (!user?.id) return;
    try {
      await fetch(`${API_URL}/api/auth/quick-links/user/${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ links })
      });
      // Update local cache
      localStorage.setItem(`quickLinks_${user.id}`, JSON.stringify(links));
    } catch (err) {
      console.error('Error saving quick links:', err);
    }
  };

  // Fetch backend devo data (deferred)
  useEffect(() => {
    if (!hasMounted || !user?.church_id) return;
    const fetchDevoData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/devotions/current-week?church_id=${user.church_id}`);
        if (res.ok) {
          const data = await res.json();
          setBackendDevoData(data);
        }
      } catch (err) {
        console.error('Error fetching devo data:', err);
      }
    };
    fetchDevoData();
  }, [hasMounted, user?.church_id]);

  // Fetch devo settings when modal opens
  const openDevoSettings = async () => {
    setShowDevoSettings(true);
    setDevoSettingsLoading(true);
    try {
      const [settingsRes, groupsRes] = await Promise.all([
        fetch(`${API_URL}/api/devotions/settings?church_id=${user.church_id}`),
        fetch(`${API_URL}/api/groups?church_id=${user.church_id}`)
      ]);
      let fetchedGroups = [];
      if (groupsRes.ok) {
        const data = await groupsRes.json();
        fetchedGroups = Array.isArray(data) ? data : (data.groups || []);
        setChurchGroups(fetchedGroups);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        // Strip stale group_ids only if we successfully fetched the groups list
        if (fetchedGroups.length > 0 && data.group_ids?.length > 0) {
          const validIds = new Set(fetchedGroups.map(g => g.id || g._id));
          data.group_ids = data.group_ids.filter(id => validIds.has(id));
        }
        setDevoSettings(data);
      }
    } catch (err) {
      console.error('Error fetching devo settings:', err);
    } finally {
      setDevoSettingsLoading(false);
    }
  };

  const saveDevoSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/devotions/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          church_id: user.church_id,
          user_id: user.id,
          post_day: devoSettings.post_day,
          group_ids: devoSettings.group_ids,
          enabled: devoSettings.enabled,
          current_week: devoSettings.current_week
        })
      });
      if (res.ok) {
        setShowDevoSettings(false);
      }
    } catch (err) {
      console.error('Error saving devo settings:', err);
    }
  };

  // Stage progress mapping (percentage complete)
  const STAGE_PROGRESS = {
    'interested': 20,
    'auditioned': 40,
    'onboarding': 60,
    'training': 80,
    'on_team': 100
  };

  // Stage display names
  const STAGE_LABELS = {
    'interested': 'INTERESTED',
    'auditioned': 'AUDITIONING',  // the board column is "Auditioning" (2026-09-09)
    'onboarding': 'ONBOARDING',
    'training': 'TRAINING',
    'on_team': 'ON TEAM'
  };
  // Studio writes stage names in sentence case (101 §7); "On Team!" keeps its case
  // only in the celebration, so here it is "On team".
  const STAGE_LABELS_STUDIO = {
    'interested': 'Interested',
    'auditioned': 'Auditioning',
    'onboarding': 'Onboarding',
    'training': 'Training',
    'on_team': 'On team'
  };

  // Stage colors
  const STAGE_COLORS = {
    'interested': 'bg-blue-500',
    'auditioned': 'bg-yellow-500',
    'onboarding': 'bg-purple-500',
    'training': 'bg-orange-500',
    'on_team': 'bg-green-500'
  };

  // Fetch user profile from MongoDB (includes avatar) with caching
  useEffect(() => {
    // Load cached profile immediately to prevent flash (synchronous)
    if (user?.id) {
      const cachedProfile = localStorage.getItem(`userProfile_${user.id}`);
      if (cachedProfile) {
        try {
          setUserProfile(JSON.parse(cachedProfile));
        } catch (e) {
          // Invalid cache, ignore
        }
      }
    }
  }, [user?.id]);
  
  // Fetch fresh user profile in background (DEFERRED)
  useEffect(() => {
    if (!hasMounted || !user?.id) return;
    
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user/profile/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setUserProfile(data);
          // Cache the profile
          localStorage.setItem(`userProfile_${user.id}`, JSON.stringify(data));
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };
    fetchUserProfile();
  }, [hasMounted, user?.id]);

  // Check if church has team members (admin/owner only — drives invite CTA)
  useEffect(() => {
    if (!hasMounted || !isAdmin || !user?.church_id) return;
    const checkTeamMembers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/church/${user.church_id}/has-team-members`);
        if (res.ok) {
          const data = await res.json();
          setHasTeamMembers(data.has_team_members);
        }
      } catch { /* ignore */ }
    };
    checkTeamMembers();
  }, [hasMounted, isAdmin, user?.church_id]);

  // Fetch onboarding candidates for dashboard widget (DEFERRED)
  useEffect(() => {
    if (!hasMounted) return; // Defer until after initial render
    
    const fetchOnboardingData = async () => {
      if (!user?.church_id) return;
      try {
        const response = await fetch(`${API_URL}/api/onboarding/candidates/${user.church_id}`, { headers: { 'X-User-Id': user.id } });
        if (response.ok) {
          const data = await response.json();
          const candidates = data.candidates || {};
          
          // Get all candidates except on_team and count pending
          let allCandidates = [];
          let pending = 0;
          
          ['interested', 'auditioned', 'onboarding', 'training'].forEach(stage => {
            const stageCandidates = candidates[stage] || [];
            pending += stageCandidates.length;
            allCandidates = [...allCandidates, ...stageCandidates.map(c => ({
              ...c,
              stage: stage
            }))];
          });
          
          // Sort by created_at (most recent first) and take top 2
          allCandidates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          // Studio lists three (the mockup); classic still renders two — see the slice below.
          setOnboardingCandidates(allCandidates.slice(0, 3));
          setPendingCount(pending);
        }

        // Alternate Onboarding Tracks (BETA): fetch per-track counts for the
        // combined breakdown. Failure here never affects the Main widget.
        try {
          const tRes = await fetch(`${API_URL}/api/onboarding/tracks/${user.church_id}`, { headers: { 'X-User-Id': user.id } });
          if (tRes.ok) {
            const tData = await tRes.json();
            setTrackBreakdown(tData.tracks || []);
          }
        } catch (_) { /* tracks are optional */ }
      } catch (err) {
        console.error('Error fetching onboarding data:', err);
      }
    };
    
    fetchOnboardingData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchOnboardingData, 30000);
    return () => clearInterval(interval);
  }, [hasMounted, user?.church_id]);

  // Fetch onboarding notifications only (message notifications come from global state via App.js)
  // DEFERRED - not needed for initial render
  useEffect(() => {
    if (!hasMounted || !user?.id || !user?.church_id) return;
    
    // Helper: drain any notifications accumulated in the App.js ref while Dashboard was unmounted.
    // Merges them into the current state with deduplication, then clears the ref.
    const drainPendingNotifs = (currentNotifs) => {
      if (!pendingOnboardingNotifsRef?.current?.length) return currentNotifs;
      const pending = pendingOnboardingNotifsRef.current;
      pendingOnboardingNotifsRef.current = [];
      const existingIds = new Set(currentNotifs.map(n => n.id));
      const newOnes = pending.filter(n => !existingIds.has(n.id));
      if (newOnes.length === 0) return currentNotifs;
      // Prepend new notifications and re-sort by created_at descending
      const merged = [...newOnes, ...currentNotifs];
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return merged;
    };

    // On mount, immediately merge any pending notifications that arrived while unmounted
    // (before the first API fetch completes, so the admin sees them right away)
    if (pendingOnboardingNotifsRef?.current?.length) {
      setOnboardingNotifications(prev => drainPendingNotifs(prev));
    }
    
    const fetchOnboardingNotifications = async () => {
      // Skip polling if tab is hidden (saves 50-70% of server requests at scale)
      if (document.hidden) return;
      
      try {
        const onboardingResponse = await fetch(`${API_URL}/api/onboarding/notifications/${user.church_id}?user_id=${encodeURIComponent(user.id)}`);
        if (onboardingResponse.ok) {
          const onboardingData = await onboardingResponse.json();
          const fetched = onboardingData.notifications || [];
          // After fetching from API, merge any WS notifications that arrived in the meantime
          const merged = drainPendingNotifs(fetched);
          setOnboardingNotifications(merged);
        }
      } catch (err) {
        console.error('Error fetching onboarding notifications:', err);
      }
    };
    
    // Fetch after a short delay (not blocking initial render)
    const initialFetch = setTimeout(fetchOnboardingNotifications, 500);
    
    // Poll every 10 seconds instead of 5 (reduces server load)
    const interval = setInterval(fetchOnboardingNotifications, 10000);
    
    // Fetch immediately when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchOnboardingNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for real-time onboarding notifications from WebSocket (via App.js custom event)
    const handleWsOnboarding = (e) => {
      const notif = e.detail;
      if (notif) {
        setOnboardingNotifications(prev => {
          // Deduplicate
          if (prev.some(n => n.id === notif.id)) return prev;
          return [notif, ...prev];
        });
      }
    };
    window.addEventListener('ws_onboarding_notification', handleWsOnboarding);

    return () => {
      clearTimeout(initialFetch);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('ws_onboarding_notification', handleWsOnboarding);
    };
  }, [hasMounted, user?.id, user?.church_id, pendingOnboardingNotifsRef]);

  // Initialize FeatureOS SDK for SSO identification (DEFERRED)
  useEffect(() => {
    if (!hasMounted || !user?.email) return;
    
    // Defer SDK loading by 2 seconds - not critical for initial render
    const timer = setTimeout(() => {
      // Load FeatureOS script if not already loaded
      if (!document.querySelector('script[src="https://worship-leader-os.featureos.app/hellonext.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://worship-leader-os.featureos.app/hellonext.js';
        script.async = true;
        script.onload = () => {
          // eslint-disable-next-line no-undef
          hellonext("init", {
            appId: "ab25ca7202fefa8ac694604e391e5a4a",
            domain: "worship-leader-os",
            user: {
              email: user.email,
              name: user.name || user.user_metadata?.name || user.email.split('@')[0],
              custom_fields: {},
            }
          });
        };
        document.head.appendChild(script);
      } else if (typeof hellonext !== 'undefined') {
        // Script already loaded, reinitialize with current user
        // eslint-disable-next-line no-undef
        hellonext("init", {
          appId: "ab25ca7202fefa8ac694604e391e5a4a",
          domain: "worship-leader-os",
          user: {
            email: user.email,
            name: user.name || user.user_metadata?.name || user.email.split('@')[0],
            custom_fields: {},
          }
        });
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [hasMounted, user?.email, user?.name, user?.user_metadata?.name]);

  // Copy intake form link to clipboard
  const copyIntakeLink = () => {
    if (user?.church_slug) {
      const link = `${window.location.origin}/intake/${user.church_slug}`;
      
      // Fallback method using textarea (works when Clipboard API is blocked)
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
      
      document.body.removeChild(textArea);
    }
  };

  // 52 weeks of Team Actions and Devotion PDFs
  const weeklyContent = [
    { actions: ['Message one team member an encouraging verse.'], pdfUrl: 'https://drive.google.com/file/d/1rAzUUQOPtR5Ps8GOTtTafdvAxNDF69tx/view?usp=share_link' },
    { actions: ['Call a team member and ask how you can pray'], pdfUrl: 'https://drive.google.com/file/d/1earBuPvXvlssDblOGICZR8aKc59XbrCR/view?usp=share_link' },
    { actions: ['Send a sincere thank you email to the whole team.'], pdfUrl: 'https://drive.google.com/file/d/17GLXNLNT6hadvfomtgn--bQt1K8Bh6Ri/view?usp=share_link' },
    { actions: ['Continue doing the Team Devos at rehearsal.'], pdfUrl: 'https://drive.google.com/file/d/1SORagMa3HPP72YnZBDQjcMWN7WRqGMpV/view?usp=share_link' },
    { actions: ['Take a team member out to coffee for a connect'], pdfUrl: 'https://drive.google.com/file/d/1iGplxyazONjJ9pU9HlLtCnGErmzDxoUJ/view?usp=share_link' },
    { actions: ['Record a 2-minute \'scriptural encouragement\' video on your phone and email it to your team.'], pdfUrl: 'https://drive.google.com/file/d/1k97gtMpjk3klLC8MKPEeXM8U0XCc19Hs/view?usp=share_link' },
    { actions: ['Privately pray for 2 team members by name'], pdfUrl: 'https://drive.google.com/file/d/1dTi3q_jpPSzwW8IUFG2ADw6Ml9diWjtR/view?usp=share_link' },
    { actions: ['Invite a team member in for a one-on-one musical training session'], pdfUrl: 'https://drive.google.com/file/d/1sh8zbq9B8xdgW0VsmA39u-qr4ODTHLX9/view?usp=share_link' },
    { actions: ['Type out an actual prayer for a team member (you to God) and then send it to them via text.'], pdfUrl: 'https://drive.google.com/file/d/1BQTVi4Dp7xeT6jpEyyv6fBMH57rjRCaU/view?usp=share_link' },
    { actions: ['Ask your team members to pray for new musicians (and recommend any they might know)'], pdfUrl: 'https://drive.google.com/file/d/1Asv1e36mr9DTbWZraOGzZjECDcAscfai/view?usp=share_link' },
    { actions: ['Email a word of thanks to your musicians for preparing and always working hard.'], pdfUrl: 'https://drive.google.com/file/d/1okbLa_G4-t-FQ-RrwHQiuUflvJ35RU3j/view?usp=share_link' },
    { actions: ['Continue doing the team devos at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1P9sRm0WnF0cogsWkWwhXGfSnMSY0VQZN/view?usp=share_link' },
    { actions: ['On Sunday PM, text that weekend\'s team a livestream link of the service and a note of thanks and encouragement.'], pdfUrl: 'https://drive.google.com/file/d/147aFt6e_QiPJPw5cq0J5LJFLnV9TnBHC/view?usp=share_link' },
    { actions: ['Take a team member out to lunch'], pdfUrl: 'https://drive.google.com/file/d/1TdCbPOJHKD-fBncCVuclvr1O_4ZA65sf/view?usp=share_link' },
    { actions: ['Write and mail handwritten notes of thanks to 4 team members'], pdfUrl: 'https://drive.google.com/file/d/1U1lrlcFZIPPtU2e2G_F2MjPe_-1nbSRq/view?usp=share_link' },
    { actions: ['Call a team member to check in'], pdfUrl: 'https://drive.google.com/file/d/1oZ1ajgAV3pJvSucUDKBQpi_BNSuSbTfO/view?usp=share_link' },
    { actions: ['Continue doing team devos at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1tBa3R8wSzGl3CVpsorBET5yk8evpKQTw/view?usp=share_link' },
    { actions: ['Type out an actual prayer for a team member (you to God) and then send it to them via text.'], pdfUrl: 'https://drive.google.com/file/d/1sbTWdDMmBeLn0sRfG6KlPM-NG7rrwVcm/view?usp=share_link' },
    { actions: ['Privately pray for 2 team members by name'], pdfUrl: 'https://drive.google.com/file/d/1xkyP4Nrng1QUDL9Ckf4aubZE00D41pp5/view?usp=share_link' },
    { actions: ['Invite a team member in for a one-on-one musical training session'], pdfUrl: 'https://drive.google.com/file/d/1L_vDAm8q9tEib0AkPMrmvrRupdi3Iklf/view?usp=share_link' },
    { actions: ['Email a word of thanks to your musicians for preparing and always working hard.'], pdfUrl: 'https://drive.google.com/file/d/1LlOj3iYmaL4uk7p97E8rZ7jbs-s3FP87/view?usp=share_link' },
    { actions: ['Send a team member an encouraging voice memo'], pdfUrl: 'https://drive.google.com/file/d/1HAS-VDPZKImTfACEh1FbpZpuzI3HGXhm/view?usp=share_link' },
    { actions: ['Privately pray for 2 team members by name'], pdfUrl: 'https://drive.google.com/file/d/1ipjZLckscGVxyJZ88vg6mgyhRilpm_Ic/view?usp=share_link' },
    { actions: ['Write and mail handwritten notes of thanks to 4 team members'], pdfUrl: 'https://drive.google.com/file/d/1vJ3g_m8THJa0v9EdVrwtOpSBfoTJrD7v/view?usp=share_link' },
    { actions: ['On Sunday PM, text that weekend\'s team a livestream link of the service and a note of thanks and encouragement.'], pdfUrl: 'https://drive.google.com/file/d/1k_a34C1rMj1olFz8bpTLCrG683JfgzTv/view?usp=share_link' },
    { actions: ['Take a team member out to coffee'], pdfUrl: 'https://drive.google.com/file/d/1DuBEbaHTS-ST3nx-6VedcUssecvfloN3/view?usp=share_link' },
    { actions: ['Email a word of thanks to your musicians for preparing and always working hard.'], pdfUrl: 'https://drive.google.com/file/d/1Fna5GRnGRpdcZu9mYVap132xO8_KAU7X/view?usp=share_link' },
    { actions: ['Type out an actual prayer for a team member (you to God) and then send it to them via text.'], pdfUrl: 'https://drive.google.com/file/d/19qiG73ohufS_GYYEhDR4PHMF3ujIkaA-/view?usp=share_link' },
    { actions: ['Greet each team member by name, and thank them when they arrive at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1nq8QHChVZL7dyHB1wNTXw7wnEH8gg0o5/view?usp=share_link' },
    { actions: ['Continue doing team devos at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1OBMehW6DhR7f47d_QdXusAD2qS0kvDDy/view?usp=share_link' },
    { actions: ['Privately pray for 2 team members by name'], pdfUrl: 'https://drive.google.com/file/d/1SloYIfkTFp95Xjo1LupeEHY2XY8n9b5U/view?usp=share_link' },
    { actions: ['Message one team member an encouraging verse.'], pdfUrl: 'https://drive.google.com/file/d/18O1lLf8chS86bsu-qiKFl-OOnPitACBf/view?usp=share_link' },
    { actions: ['Take a team member out to lunch'], pdfUrl: 'https://drive.google.com/file/d/1uKS8wEMwOUXmDQuVufNa72MsnHMb-cVv/view?usp=share_link' },
    { actions: ['Record a 2-minute \'scriptural encouragement\' video on your phone and email it to your team.'], pdfUrl: 'https://drive.google.com/file/d/1KWr7b8pQyYGLRquhZo9RKdIODjS9bOvS/view?usp=share_link' },
    { actions: ['Take a team member out to coffee for a connect'], pdfUrl: 'https://drive.google.com/file/d/1q1Of0t0ACkbXJz6BqYdtZT3MVl40WrFN/view?usp=share_link' },
    { actions: ['Buy your most faithful team member a gift card to their favorite coffee place'], pdfUrl: 'https://drive.google.com/file/d/1rbNrN36MszokNvJ3my-jW0QIuD6Ccx5K/view?usp=share_link' },
    { actions: ['Message one team member an encouraging verse.'], pdfUrl: 'https://drive.google.com/file/d/1475WMMWxgi8mc0QgQCONbzkysCSz43Co/view?usp=share_link' },
    { actions: ['Text one team member a specific reason you\'re thankful for them'], pdfUrl: 'https://drive.google.com/file/d/1Pzax8WzX_s_GVwQ6wiD7t4O1YYiS5HiV/view?usp=share_link' },
    { actions: ['Ask one team member how you can be praying for them'], pdfUrl: 'https://drive.google.com/file/d/15bfvs7ICr2DOihz37zIb-VpUTV0Oro3b/view?usp=share_link' },
    { actions: ['Call one team member to check in'], pdfUrl: 'https://drive.google.com/file/d/1WqZ623FZHzKlc-biDEcOVwpnsi9QB_BF/view?usp=share_link' },
    { actions: ['Privately pray for 2 team members by name'], pdfUrl: 'https://drive.google.com/file/d/18f_zgLo_2mpICuQg7U-j5XtSsl-1EjYC/view?usp=share_link' },
    { actions: ['Message one team member an encouraging verse.'], pdfUrl: 'https://drive.google.com/file/d/1Z2_ybs1AhBMocj8OJETsrRUS02rrHisw/view?usp=share_link' },
    { actions: ['Type out an actual prayer for a team member (you to God) and then send it to them via text.'], pdfUrl: 'https://drive.google.com/file/d/1Ou2Y8_oPATY6as8OQKFasGBiXOdG8fSq/view?usp=sharing' },
    { actions: ['Continue doing the Team Devos at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1WJ5tl4PzSK6aTHoS_cID0_mePPvM_O8E/view?usp=share_link' },
    { actions: ['Send 5 team members individual texts telling them why you\'re grateful for them.'], pdfUrl: 'https://drive.google.com/file/d/1VJE8ICqR_hxANHuK9hTSS1YHr6tN_eRm/view?usp=share_link' },
    { actions: ['Give positive feedback to 2 team members at rehearsal'], pdfUrl: 'https://drive.google.com/file/d/1OnmGiynDxqLnJQtIzgPb4JIBUDGF8Uvf/view?usp=share_link' },
    { actions: ['Email a word of thanks to your musicians for preparing and always working hard.'], pdfUrl: 'https://drive.google.com/file/d/144FPTVMKn4h54AOuye7tNVnY65nnMmri/view?usp=share_link' },
    { actions: ['Take a team member out to coffee for a connect'], pdfUrl: 'https://drive.google.com/file/d/1Hzk30jKIflVbjSziVZcyWpO_72UiOrxG/view?usp=share_link' },
    { actions: ['Pray for one team member as a group after Sunday service.'], pdfUrl: 'https://drive.google.com/file/d/1ov9EjGZo7QJnpTDhyf12WDjpyMqALi6F/view?usp=share_link' },
    { actions: ['Record a 2-minute \'scriptural encouragement\' video on your phone and email it to your team.'], pdfUrl: 'https://drive.google.com/file/d/1rrANJZWYNgBQT-9IReKPrF5ZXexttdGb/view?usp=share_link' },
    { actions: ['Take 3 or 4 team members out to lunch.'], pdfUrl: 'https://drive.google.com/file/d/1Bmv1DXWkZIP2DEo5CSjHcfdXWbWlQpIP/view?usp=share_link' },
    { actions: ['Call a team member to check in'], pdfUrl: 'https://drive.google.com/file/d/13UGclXRnLKlwf0o5-m2n06s5FtsbyNdq/view?usp=share_link' },
  ];

  // Devo content from backend (single source of truth) with local fallback
  const [backendDevoData, setBackendDevoData] = useState(null);
  
  // Local calculation as fallback until backend responds
  const getChurchDevoWeek = () => {
    if (!user?.church_created_at) {
      return 1;
    }
    // TRN-Q7 / DSH-Q13: church_created_at is a naive-UTC ISO string with no zone
    // designator, which JS would read as browser-local time. Pin it to UTC first,
    // then move BOTH it and "now" into the church wall clock so the weekday and
    // the week arithmetic happen in one frame (the server does the same).
    const raw = String(user.church_created_at);
    const createdAtUtc = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw) ? raw : `${raw}Z`;
    if (Number.isNaN(new Date(createdAtUtc).getTime())) {
      return 1;
    }
    const toChurchTime = (d) => (churchTimezone
      ? new Date(new Date(d).toLocaleString('en-US', { timeZone: churchTimezone }))
      : new Date(d));
    const createdAt = toChurchTime(createdAtUtc);
    const now = toChurchTime(new Date());
    const firstSunday = new Date(createdAt);
    const dayOfWeek = createdAt.getDay();
    if (dayOfWeek === 0) {
      firstSunday.setDate(createdAt.getDate() + 7);
    } else {
      firstSunday.setDate(createdAt.getDate() + (7 - dayOfWeek));
    }
    firstSunday.setHours(0, 0, 0, 0);
    if (now < firstSunday) {
      return 1;
    }
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const weeksElapsed = Math.floor((now - firstSunday) / msPerWeek);
    return ((weeksElapsed + 1) % weeklyContent.length) + 1;
  };

  const currentWeek = backendDevoData?.week_number || getChurchDevoWeek();
  const weekIndex = ((currentWeek - 1) % weeklyContent.length + weeklyContent.length) % weeklyContent.length;
  const currentWeekContent = backendDevoData 
    ? { actions: backendDevoData.actions, pdfUrl: backendDevoData.pdf_url }
    : weeklyContent[weekIndex];

  // Define fetch functions BEFORE the useEffect that uses them
  const fetchVerseOfTheDay = async () => {
    try {
      const params = user?.church_id ? `?church_id=${user.church_id}` : '';
      const response = await fetch(`${API_URL}/api/verse-of-the-day${params}`);
      if (response.ok) {
        const data = await response.json();
        setVerseOfTheDay(data);
      }
    } catch (err) {
      console.error('Error fetching verse of the day:', err);
    }
  };

  const fetchTodaysTasks = async () => {
    try {
      const { data, error } = await fetchAllTasks(supabase, user.id);

      if (!error && data && setGlobalTasks) {
        setGlobalTasks(data);
        setIsLoadingTasks(false);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    }
  };

  // Fetch tasks only if not already loaded from cache
  useEffect(() => {
    if (globalTasks.length === 0) {
      fetchTodaysTasks();
    } else {
      setIsLoadingTasks(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, globalTasks.length]);
  
  // Fetch verse of the day (DEFERRED - not critical for initial render)
  useEffect(() => {
    if (!hasMounted) return;
    fetchVerseOfTheDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted]);

  // Fetch church timezone for devo week calculation
  useEffect(() => {
    if (!user?.church_id) return;
    fetch(`${API_URL}/api/comms/v3/church/timezone/${user.church_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.timezone) setChurchTimezone(data.timezone); })
      .catch(() => {});
  }, [user?.church_id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addQuickTask = async () => {
    try {
      // The dialog's Scheduled Date wins; otherwise today (2026-09-09).
      const today = format(new Date(), 'yyyy-MM-dd');
      const dateStr = newTask.date || today;
      if (newTask.assigned_to) {
        // Shared task: one synced record visible to creator + assignee
        await createSharedItem({
          item_type: 'task',
          title: newTask.title,
          description: newTask.description || '',
          assigned_to: newTask.assigned_to,
          due_date: dateStr,
          priority_level: newTask.priority || 'none',
          subtasks: (newTask.subtasks || []).map(s => ({ id: s.id, title: s.title, completed: false })),
          // Recurring shared task: the server keeps ONE live occurrence and
          // spawns the next when this one is ticked off.
          ...(newTask.isRecurring ? { is_recurring: true, ...recurrencePayloadFields(newTask) } : {}),
        });
        toast.success(`Task assigned to ${newTask.assigned_to_name || 'teammate'}`);
        setNewTask(INITIAL_TASK_STATE);
        setShowAddTask(false);
        return;
      }
      const newTasks = await createPlannerTask({
        userId: user.id,
        newTask,
        date: dateStr,
        supabase,
      });
      if (newTask.subtasks?.length && newTasks[0]?.id) {
        await subtasksApi.saveItems(newTasks[0].id, newTask.subtasks);
      }
      if (setGlobalTasks) {
        setGlobalTasks(prev => [...prev, ...newTasks]);
      }
      if (dateStr !== today) {
        toast.success(`Added to ${format(new Date(dateStr + 'T00:00:00'), 'EEE, MMM d')}`);
      }
      setNewTask(INITIAL_TASK_STATE);
      setShowAddTask(false);
    } catch (err) {
      console.error('Error adding task:', err);
      // Shared-task writes come back with a real reason (e.g. "A repeating
      // task needs a due date") — show it rather than the generic line.
      toast.error((newTask.assigned_to && err?.message) || "Couldn't add your task. Please try again.");
    }
  };

  const toggleTask = async (taskId, completed, opts = {}) => {
    const next = !completed;
    // Optimistic: animate/update immediately, write in background, revert on failure.
    let reverted = false;
    if (next) {
      setCompletingTaskId(taskId);
      setTimeout(() => {
        if (reverted) return;
        if (setGlobalTasks) {
          setGlobalTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed: true } : t));
        }
        setCompletingTaskId(null);
      }, 500);
    } else {
      if (setGlobalTasks) {
        setGlobalTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed: false } : t));
      }
    }
    try {
      const { error } = await supabase.from('tasks').update({ completed: next })
        .eq('id', taskId).abortSignal(writeTimeoutSignal());
      if (error) throw error;
      if (!opts.silent) {
        const title = opts.title || 'Task';
        pushUndo(
          next ? `"${title}" completed` : `"${title}" unchecked`,
          () => toggleTask(taskId, next, { silent: true, title }),
          next ? { toastMessage: 'Task completed' } : {}
        );
      }
    } catch (err) {
      console.error('Error toggling task:', err);
      reverted = true;
      setCompletingTaskId(null);
      if (setGlobalTasks) {
        setGlobalTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed } : t));
      }
      if (opts.silent) throw err; // undo path: let undoStack show its own failure toast
    }
  };

  const getUserAvatar = () => {
    // Use unified UserAvatar component
    // Priority: user prop avatar (updated via onUpdateUser) > userProfile > user_metadata
    const avatarUrl = user?.avatar;
    return (
      <UserAvatar 
        userId={user?.id}
        name={user?.name || user?.user_metadata?.name || user?.email}
        avatarUrl={avatarUrl}
        size="md"
        className="w-full h-full"
      />
    );
  };

  // P2-l (DIV-042): real tier names from GET /stripe/subscription (App.js sets
  // currentPlan). 'none' = no subscription on file.
  const getPlanBadge = () => {
    const plans = {
      none: { label: 'No plan', className: 'bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300' },
      starter: { label: 'Starter', className: 'bg-slate-100 dark:bg-[#3a3d41] text-slate-700 dark:text-slate-200' },
      growth: { label: 'Growth', className: 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300' },
      pro: { label: 'Pro', className: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' },
      mega: { label: 'Mega', className: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' },
      ultra: { label: 'Ultra', className: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' },
    };
    return plans[currentPlan] || plans.none;
  };

  // Show Email Flows Manager for super admin
  if (showEmailFlows) {
    return (
      <EmailFlowsManager 
        userEmail={user?.email} 
        onClose={() => setShowEmailFlows(false)} 
      />
    );
  }

  // Show Knowledge Base Manager for super admin
  if (showKnowledgeBase) {
    return (
      <KnowledgeBaseManager 
        userEmail={user?.email}
        onClose={() => setShowKnowledgeBase(false)} 
      />
    );
  }

  // Show Announcements Manager for super admin
  if (showAnnouncements) {
    return (
      <AnnouncementsManager 
        userEmail={user?.email}
        onClose={() => setShowAnnouncements(false)} 
      />
    );
  }

  // Show Quarterly Workshops Manager for super admin
  if (showWorkshops) {
    return (
      <WorkshopsManager
        userEmail={user?.email}
        onClose={() => setShowWorkshops(false)}
      />
    );
  }

  // Show System Health dashboard for super admin
  if (showHealth) {
    return <HealthDashboard user={user} onClose={() => setShowHealth(false)} />;
  }

  // Show Training Defaults Manager for super admin
  // (Removed — replaced by the one-click "Publish to all churches" button on the
  // Customize Training page. The super-admin's account training IS the master.)

  // ---- 104 · view model for the transcribed Studio dashboard ------------------
  // Every value here already exists above; the Studio view is a pure render of it,
  // so behaviour, fetching and testids are identical to the classic grid.
  const studioUnread = teamCommsNotifications.filter(n => !n.is_read && n.sender_id !== user?.id).length;
  const studioVm = {
    todayTasks,
    completingTaskId,
    toggleTask,
    handleEditTask,
    openAddTask: () => setShowAddTask(true),
    formatTime12h,
    // D2 (121 §5, owner 2026-09-23): the sub-task chip returns to the Studio row
    // with classic's behaviour — the same store, the same expand set, the same
    // handler as the classic row (Dashboard.js:2064). Studio-only: `studioVm` is
    // read by StudioDashboardView and by nothing else.
    subtasksApi,
    expandedSubtasks,
    toggleSubtaskExpand,
    sharedTasksNode: mySharedTasks.length > 0 ? (
      <SharedTasksSection
        tasks={mySharedTasks}
        currentUserId={user?.id}
        onToggle={toggleSharedItem}
        onDelete={deleteSharedItem}
        onEdit={setEditingSharedTask}
        highlightId={highlightSharedId}
        compact
        setHoveredItem={setHoveredItem}
        isOwner={sharedContext.role === 'owner'}
      />
    ) : null,

    recentComms,
    formatTimeAgo,
    stripHtml,
    avatarForComms: (item) => (item.type === 'post'
      ? (item.author?.avatar_url || item.author?.avatar)
      : (getCachedAvatar(item.author?.id) || item.author?.avatar)),
    openComms: () => onOpenApp('comms'),
    openCommsItem: (item) => (item.type === 'message'
      ? onOpenApp('comms', 'messages', item.conversationId, null, true, null, item.id)
      : onOpenApp('comms', 'feed', item.id)),
    openQuickCompose: () => setShowQuickCompose(true),
    unreadComms: studioUnread,

    aiMessage,
    setAiMessage,
    // §45: the three most recent conversations, newest first (the endpoint sorts
    // by `updated_at` desc). D18 (121 §5) closes question U1: a row now opens the
    // AI app *on that conversation*. `openAi` keeps the prompt in its first slot
    // (the two chips pass one) and takes the conversation id in a second, so the
    // brief's `openAi(conversationId)` is spelled `openAi(null, id)` — one
    // argument per meaning rather than one slot with two. It reaches
    // `WorshipAIChat` through App.js's existing `handleOpenApp`, in its new
    // trailing `options` object; no route, endpoint or fetch is added, and the app
    // selects the conversation with the same `loadConversation` the sidebar uses.
    aiConversations,
    openAi: (prompt, conversationId) => {
      if (conversationId) return onOpenApp('aichat', null, null, null, true, null, null, { conversationId });
      return prompt ? onOpenApp('aichat', null, prompt, null, false) : onOpenApp('aichat');
    },
    askAi: () => {
      if (aiMessage.trim()) { onOpenApp('aichat', null, aiMessage.trim()); setAiMessage(''); }
      else { onOpenApp('aichat'); }
    },
    // The mock's right slot reads "42 of 60 credits left". Owner (2026-09-10):
    // do what classic does — classic shows nothing there — so the slot is empty.
    aiCreditsLabel: null,

    weekActions: currentWeekContent.actions,
    featuredWorkshop,
    openWorkshop: () => spaNavigate(`/${user.church_slug}/training/workshop`),
    downloadDevo: () => window.open(currentWeekContent.pdfUrl, '_blank', 'noopener,noreferrer'),
    devoEnabled: !!devoSettings.enabled,
    devoDisabled: !devoSettingsFetched,
    toggleDevoAutoPost,
    openDevoSettings,
    openTrainingLibrary: () => spaNavigate(`/${user.church_slug}/training`),
    isAdmin,
    hasChurchSlug: !!user?.church_slug,

    pipelineTotal: pendingCount + trackBreakdown.reduce((n, t) => n + (t.candidate_count || 0), 0),
    trackChips: [
      { id: 'main', name: 'Main team', count: pendingCount },
      ...trackBreakdown.map(t => ({ id: t.id, name: t.name, count: t.candidate_count || 0 })),
    ],
    onboardingCandidates,
    candidateRole: (c) => resolveAreaLabel((c.areas_of_interest || [])[0] || c.area_of_interest || '', null),
    candidateStage: (c) => STAGE_LABELS_STUDIO[c.stage] || c.stage,
    candidateProgress: (c) => STAGE_PROGRESS[c.stage] || 20,
    openOnboarding: () => onOpenApp('onboarding'),
    openCandidate: (id) => onOpenApp('onboarding', id),
    copyIntakeLink,
    copiedLink,

    stats: [
      { label: 'Tasks today', value: todayTasks.length },
      { label: 'Completed', value: todayTasks.filter(t => t.completed).length },
      { label: 'Onboarding', value: pendingCount },
      { label: 'Messages', value: studioUnread, onClick: () => onOpenApp('messaging') },
    ],
    openInvite: () => onOpenSettings('team'),
    quickLinks,
    openLinkManager: () => setShowAddLinkDialog(true),
    openMessages: () => onOpenApp('messaging'),

    verse: verseOfTheDay,
    onOpenPlanner: (view) => onOpenApp('planner', view),

    // Slot 6 · the same three states as the classic grid: the skeleton while the
    // connection status is unknown, the Planning Center widget when connected,
    // otherwise Quick stats + Quick links (119, mock frame 3).
    pcLoading: planningCenterLoading,
    pcWidget: (!planningCenterLoading && planningCenterConnected) ? (
      <PlanningCenterWidget
        userId={user?.id}
        onReconnect={connectPlanningCenter}
        onQuickMessage={(servicePlan) => {
          setPcoServicePlan(servicePlan);
          setShowQuickCompose(true);
        }}
      />
    ) : null,
  };

  return (
    <>
    <div data-ui="page" className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33]">
      {/* Minimized Header */}
      <header className="bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 sticky top-0 z-20">
        <div className={isStudio ? "st-hdr st-db-hdr" : "max-w-7xl mx-auto px-6 py-2.5"}>
          <div className="flex items-center justify-between">
            <div className={isStudio ? "flex items-center gap-[14px]" : "flex items-center gap-2.5"}>
              {isStudio ? (
                <div
                  data-ui="brand-mark"
                  aria-label="Worship OS"
                  className="w-8 h-8 flex items-center justify-center font-bold text-[15px] flex-shrink-0"
                >W</div>
              ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_praiseleadsys/artifacts/dqkpm4bx_Untitled%20design%20%284%29.png" 
                alt="Worship OS" 
                className="w-10 h-10 rounded-xl object-contain"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
              />
              )}
              {isStudio ? (
                <div className="min-w-0">
                  <h1 className="st-db-greet text-foreground">
                    {studioGreeting}, {firstName} 👋
                  </h1>
                  <p className="st-db-sub truncate">
                    {[user?.church_name, studioDate].filter(Boolean).join(' · ')}
                  </p>
                </div>
              ) : (
                <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                  Hey, {firstName}!
                </h1>
              )}
              {!isStudio && isAdmin && currentPlan && currentPlan !== 'free' && (
                <button
                  type="button"
                  onClick={() => onOpenSettings?.('billing')}
                  title="Your plan — open Billing"
                  data-testid="plan-badge"
                  className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getPlanBadge().className}`}
                >
                  {getPlanBadge().label}
                </button>
              )}
              {isAdmin && (
                <TeamBonusCallout user={user} onOpenSettings={onOpenSettings} />
              )}
            </div>
            <div className="flex items-center gap-2">
              {isStudio && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden md:inline-flex h-8 gap-2 px-2.5 text-muted-foreground font-medium"
                  onClick={() => setShowQuickAdd?.(true)}
                >
                  <Plus className="w-4 h-4" />
                  Quick add
                  <kbd className="ml-0.5 border border-input rounded px-1.5 py-px text-[11px] font-medium text-muted-foreground">⌘ Space</kbd>
                </Button>
              )}
              {/* Notification Bell with Popover */}
              <div className="relative" ref={notifRef}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="relative h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-white/10 transition-colors bell-hover-wiggle"
                        data-testid="notification-bell"
                      >
                        <Bell className={`w-4 h-4 text-slate-600 dark:text-slate-300 bell-hover-target ${bellRinging ? 'bell-ring' : ''}`} />
                        {bellUnread > 0 && (
                          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                            {bellUnread > 9 ? '9+' : bellUnread}
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Notifications</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                {/* Notification Dropdown - Using Shared Component */}
                {showNotifications && (
                  <NotificationPanel
                    notifications={teamCommsNotifications || []}
                    onboardingNotifications={onboardingNotifications}
                    superAdminNotifications={isSuperAdmin ? superAdminNotifications : []}
                    user={user}
                    excludeOnboarding={false}
                    unreadCount={bellUnread}
                    API_URL={API_URL}
                    onClose={() => setShowNotifications(false)}
                    onMarkAllRead={async () => {
                      // Mark all comms notifications via hook
                      if (markAllRead) markAllRead();
                      // Mark all onboarding notifications
                      setOnboardingNotifications(prev => prev.map(n => ({ ...n, read: true })));
                      if (user?.church_id) {
                        fetch(`${API_URL}/api/onboarding/notifications/mark-all-read/${user.church_id}?user_id=${encodeURIComponent(user.id)}`, { method: 'POST' }).catch(() => {});
                      }
                      // Mark all super-admin alerts
                      if (isSuperAdmin && superAdminNotifications.some(n => !n.is_read)) {
                        setSuperAdminNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                        fetch(`${API_URL}/api/admin-churches/notifications/mark-read?user_email=${encodeURIComponent(user.email)}`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ all: true })
                        }).catch(() => {});
                      }
                    }}
                    onNotificationClick={async (notification) => {
                      try {
                        if (notification.source === 'super_admin') {
                          // Mark read + deep-link into Church Management to act on it
                          setSuperAdminNotifications(prev => prev.map(n =>
                            n.id === notification.id ? { ...n, is_read: true } : n
                          ));
                          fetch(`${API_URL}/api/admin-churches/notifications/mark-read?user_email=${encodeURIComponent(user.email)}`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: notification.id })
                          }).catch(() => {});
                          try {
                            sessionStorage.setItem('superAdminBonusTarget', JSON.stringify({
                              church_id: notification.church_id, owner_email: notification.owner_email,
                            }));
                          } catch { /* ignore */ }
                          onOpenSettings?.('app-stats');
                        } else if (notification.source === 'onboarding') {
                          setOnboardingNotifications(prev => prev.map(n => 
                            n.id === notification.id ? { ...n, read: true } : n
                          ));
                          fetch(`${API_URL}/api/onboarding/notifications/${notification.id}/read?user_id=${encodeURIComponent(user?.id || '')}`, { method: 'POST' });
                          if (notification.type === 'member_joined') {
                            // A new user just joined — land on Team Comms → Groups
                            // so admin can add them to relevant groups.
                            onOpenApp('comms', 'groups');
                          } else {
                            onOpenApp('onboarding', notification.candidate_id);
                          }
                        } else if (notification.source === 'comms') {
                          // Mark all notifications in this group as read via hook
                          const groupIds = notification._groupIds || [notification.id];
                          if (markReadByIds) markReadByIds(groupIds);
                          
                          // Navigate based on notification type
                          if (['service_request','service_response','service_updated','service_comment'].includes(notification.type)) {
                            // Fix 7 G4: admins land in the Services app; anyone else in Team Comms → Services.
                            if (servicesAdminFor(user)) {
                              onOpenApp('services', { tab: 'plans', serviceId: notification.service_id || null });
                            } else {
                              onOpenApp('comms', 'services', null, null, true, null, null, { serviceHighlight: { serviceId: notification.service_id || null, type: notification.type } });
                            }
                          } else if (notification.type === 'planner_review') {
                            // Quarterly/annual wins review — open planner with the
                            // Trophy Drawer in review mode.
                            try { sessionStorage.setItem('plannerStartReview', '1'); } catch { /* ignore */ }
                            onOpenApp('planner', 'week');
                          } else if (notification.type === 'workshop') {
                            // New quarterly team workshop — go straight to the page
                            if (notification.workshop_url) {
                              spaNavigate(notification.workshop_url);
                            } else if (user.church_slug) {
                              spaNavigate(`/${user.church_slug}/training/workshop`);
                            }
                          } else if (notification.type === 'dm' || notification.type === 'message_reaction') {
                            const conversationId = notification.conversation_id;
                            const messageId = notification.message_id;
                            if (conversationId) {
                              if (markMessagesAsRead && messageId) {
                                markMessagesAsRead(user.id, [messageId], conversationId);
                              }
                              onOpenApp('comms', 'messages', conversationId, null, true, null, messageId);
                            } else {
                              onOpenApp('comms', 'messages');
                            }
                          } else if (notification.type === 'shared_planner') {
                            if (notification.shared_item_type === 'task') {
                              // Task lives right here in the dashboard's shared bubble
                              setHighlightSharedId(notification.shared_item_id || null);
                              setTimeout(() => setHighlightSharedId(null), 3500);
                            } else {
                              // Goals/priorities live in the Planner
                              const params = new URLSearchParams();
                              if (notification.shared_item_id) params.set('shared_highlight', notification.shared_item_id);
                              params.set('shared_type', notification.shared_item_type || 'goal');
                              spaNavigate(`/planner?${params.toString()}`);
                            }
                          } else if (notification.type === 'post_reaction') {
                            onOpenApp('comms', 'feed', null, notification.post_id, true, null, null);
                          } else {
                            onOpenApp('comms', 'feed', null, notification.post_id, true, notification.comment_id, null);
                          }
                        }
                      } catch (err) {
                        console.error('Error handling notification click:', err);
                      }
                    }}
                  />
                )}
              </div>
              
              {/* User Menu Dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={isStudio
                    ? 'st-db-avbtn transition-colors'
                    : 'flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-colors'}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-[#3e4145] flex items-center justify-center text-slate-500 dark:text-slate-400 font-semibold text-sm overflow-hidden">
                    {getUserAvatar()}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 dark:text-slate-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="pop-in absolute right-0 top-full mt-2 w-72 bg-card rounded-2xl shadow-2xl border border-slate-200/80 py-2 z-50 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="user-menu-dropdown">
                    {isStudio && (
                      <div className="flex items-center gap-2.5 px-4 pt-2 pb-3 mb-1 border-b border-border">
                        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-sm overflow-hidden flex-shrink-0">
                          {getUserAvatar()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-medium text-foreground truncate">{user?.name || firstName}</div>
                          <div className="text-[12px] text-muted-foreground truncate">{user?.email}</div>
                        </div>
                        {isAdmin && currentPlan && currentPlan !== 'free' && (
                          <button
                            type="button"
                            onClick={() => { setShowUserMenu(false); onOpenSettings?.('billing'); }}
                            title="Your plan — open Billing"
                            data-testid="plan-badge"
                            className="studio-chip flex-shrink-0"
                          >
                            {getPlanBadge().label}
                          </button>
                        )}
                      </div>
                    )}
                    {/* Menu Items */}
                    <div className="py-1 px-2">
                      <button
                        onClick={() => { setShowUserMenu(false); onOpenSettings('profile'); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Profile settings' : 'Profile Settings'}</span>
                      </button>
                      <button
                        onClick={() => { setShowUserMenu(false); onOpenSettings('notifications'); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                        data-testid="notification-settings-menu"
                      >
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                          <Bell className="w-4 h-4 text-indigo-500" />
                        </div>
                        <span className="font-medium">Notifications</span>
                      </button>
                      <button
                        onClick={() => { setShowUserMenu(false); onOpenSettings('team'); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
                          <Users className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Team management' : 'Team Management'}</span>
                      </button>
                      {user.role === 'owner' && (
                        <button
                          onClick={() => { setShowUserMenu(false); onOpenSettings('billing'); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          </div>
                          <span className="font-medium">{isStudio ? 'Billing and subscription' : 'Billing & Subscription'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => { setShowUserMenu(false); onOpenSettings('calendar'); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
                          <CalendarIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Calendar sync' : 'Calendar Sync'}</span>
                      </button>
                      {/* Planning Center: connect, or open settings when connected */}
                      <button
                        onClick={() => { 
                          setShowUserMenu(false); 
                          if (planningCenterConnected) {
                            onOpenSettings('planning-center');
                          } else {
                            connectPlanningCenter();
                          }
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                        data-testid="planning-center-toggle"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${planningCenterConnected ? 'bg-teal-100 dark:bg-teal-900/50' : 'bg-slate-100 dark:bg-[#3a3d41]'}`}>
                          {planningCenterConnected ? (
                            <Settings2 className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                          ) : (
                            <Link2 className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                          )}
                        </div>
                        <span className="font-medium">
                          {planningCenterConnected
                            ? (isStudio ? 'Planning Center settings' : 'Planning Center Settings')
                            : 'Connect Planning Center'}
                        </span>
                      </button>
                      {SUPER_ADMIN_EMAILS.some(e => e.toLowerCase() === user?.email?.toLowerCase()) && (
                        <button
                          onClick={() => { setShowUserMenu(false); onOpenSettings('app-stats'); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                          data-testid="app-stats-menu"
                        >
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                            <BarChart3 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <span className="font-medium">{isStudio ? 'App statistics' : 'App Statistics'}</span>
                        </button>
                      )}
                    </div>

                    <div className="border-t border-slate-100 dark:border-white/10 py-2 px-2 mx-2">
                      <button
                        onClick={() => { setShowUserMenu(false); setShowDownloadApp(true); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 rounded-xl transition-all duration-150 border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/10"
                        data-testid="download-app-menu"
                      >
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center">
                          <Smartphone className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Download apps' : 'Download Apps'}</span>
                      </button>
                      <button
                        onClick={() => { setShowUserMenu(false); setShowShortcuts(true); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center">
                          <Keyboard className="w-4 h-4 text-indigo-500" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Keyboard shortcuts' : 'Keyboard Shortcuts'}</span>
                      </button>
                      <ThemeMenuItem />
                      <DesignMenuItem user={user} userId={user?.id} />
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowHelpDialog(true);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
                          <HelpCircle className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Help and support' : 'Help & Support'}</span>
                      </button>
                    </div>

                    {/* Super Admin Only Section */}
                    {isSuperAdmin && (
                      <div className="border-t border-slate-100 dark:border-white/10 py-2 px-2 mx-2">
                        <div className="px-3 py-1.5">
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">{isStudio ? 'Super admin' : 'Super Admin'}</span>
                        </div>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowEmailFlows(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/15 rounded-xl transition-all duration-150"
                        >
                          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                            <Workflow className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          </div>
                          <span className="font-medium">{isStudio ? 'Email flows' : 'Email Flows'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowKnowledgeBase(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/15 rounded-xl transition-all duration-150"
                        >
                          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                            <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <span className="font-medium">{isStudio ? 'AI knowledge base' : 'AI Knowledge Base'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowAnnouncements(true);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 rounded-xl transition-all duration-150"
                        >
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                            <Megaphone className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <span className="font-medium">Announcements</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowWorkshops(true);
                          }}
                          data-testid="superadmin-workshops-btn"
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/15 rounded-xl transition-all duration-150"
                        >
                          <div className="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-900/50 flex items-center justify-center">
                            <GraduationCap className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                          </div>
                          <span className="font-medium">{isStudio ? 'Quarterly workshops' : 'Quarterly Workshops'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowHealth(true);
                          }}
                          data-testid="superadmin-system-health-btn"
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-500/15 rounded-xl transition-all duration-150"
                        >
                          <div className="relative w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                            <Activity className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                            {healthStatus && healthStatus !== 'healthy' && (
                              <span
                                data-testid="health-status-dot"
                                className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-slate-900 ${healthStatus === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`}
                              />
                            )}
                          </div>
                          <span className="font-medium">{isStudio ? 'System health' : 'System Health'}</span>
                          {healthStatus && healthStatus !== 'healthy' && (
                            <span className={`ml-auto ${isStudio ? 'text-[12px] font-medium' : 'text-[10px] font-bold uppercase'} ${healthStatus === 'critical' ? 'text-red-500' : 'text-amber-500'}`}>
                              {isStudio ? healthStatus.charAt(0).toUpperCase() + healthStatus.slice(1) : healthStatus}
                            </span>
                          )}
                        </button>
                      </div>
                    )}

                    <div className="border-t border-slate-100 dark:border-white/10 pt-2 pb-1 px-2 mx-2">
                      <button
                        onClick={() => { setShowUserMenu(false); onSignOut(); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/15 rounded-xl transition-all duration-150"
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                          <LogOut className="w-4 h-4 text-red-500 dark:text-red-400" />
                        </div>
                        <span className="font-medium">{isStudio ? 'Sign out' : 'Sign Out'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Keyboard Shortcuts Modal */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className={isStudio ? 'max-w-md sm:max-w-[400px]' : 'max-w-md'} data-ui-dialog="shortcuts">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span>{isStudio ? 'Keyboard shortcuts' : 'Keyboard Shortcuts'}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {/* Primary shortcuts */}
            <div data-ui="kbd-row" className="p-3 bg-indigo-50/70 dark:bg-indigo-500/10 rounded-xl border border-indigo-100/50 dark:border-indigo-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-200">{isStudio ? 'Quick add task' : 'Quick Add Task'}</span>
                  <p className="text-xs text-indigo-500/80 dark:text-indigo-300/80 mt-0.5">Add a task from anywhere</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <kbd className="px-2.5 py-1.5 text-xs bg-card rounded-lg border border-indigo-200 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-200 font-semibold shadow-sm">⌘</kbd>
                  <span className="text-indigo-300 dark:text-indigo-400 text-xs">+</span>
                  <kbd className="px-2.5 py-1.5 text-xs bg-card rounded-lg border border-indigo-200 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-200 font-semibold shadow-sm">Space</kbd>
                </div>
              </div>
            </div>
            <div data-testid="shortcut-quick-message" data-ui="kbd-row" className="p-3 bg-blue-50/70 dark:bg-blue-500/10 rounded-xl border border-blue-100/50 dark:border-blue-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-blue-700 dark:text-blue-200">Quick Message</span>
                  <p className="text-xs text-blue-500/80 dark:text-blue-300/80 mt-0.5">Compose a message from anywhere</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <kbd className="px-2.5 py-1.5 text-xs bg-card rounded-lg border border-blue-200 dark:border-blue-500/40 text-blue-700 dark:text-blue-200 font-semibold shadow-sm">Shift</kbd>
                  <span className="text-blue-300 dark:text-blue-400 text-xs">+</span>
                  <kbd className="px-2.5 py-1.5 text-xs bg-card rounded-lg border border-blue-200 dark:border-blue-500/40 text-blue-700 dark:text-blue-200 font-semibold shadow-sm">M</kbd>
                </div>
              </div>
            </div>
            <div data-ui="kbd-row" className="p-3 bg-red-50/70 dark:bg-red-500/10 rounded-xl border border-red-100/50 dark:border-red-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-red-700 dark:text-red-200">{isStudio ? 'Delete item' : 'Delete Item'}</span>
                  <p className="text-xs text-red-500/80 dark:text-red-300/80 mt-0.5">{isStudio ? 'Hover over a task, priority or goal' : 'Hover over task/priority/goal'}</p>
                </div>
                <kbd className="px-2.5 py-1.5 text-xs bg-card rounded-lg border border-red-200 dark:border-red-500/40 text-red-700 dark:text-red-200 font-semibold shadow-sm">Delete</kbd>
              </div>
            </div>
            
            {/* Secondary shortcuts */}
            <div className="pt-2 space-y-1">
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">{isStudio ? 'Submit form / send message' : 'Submit form / Send message'}</span>
                <div className="flex items-center gap-1.5">
                  <kbd className="px-2.5 py-1.5 text-xs bg-slate-100 dark:bg-[#3a3d41] rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold">⌘</kbd>
                  <span className="text-slate-300 text-xs">+</span>
                  <kbd className="px-2.5 py-1.5 text-xs bg-slate-100 dark:bg-[#3a3d41] rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold">Enter</kbd>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">{isStudio ? 'New conversation (AI chat)' : 'New conversation (AI Chat)'}</span>
                <div className="flex items-center gap-1.5">
                  <kbd className="px-2.5 py-1.5 text-xs bg-slate-100 dark:bg-[#3a3d41] rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold">⌘</kbd>
                  <span className="text-slate-300 text-xs">+</span>
                  <kbd className="px-2.5 py-1.5 text-xs bg-slate-100 dark:bg-[#3a3d41] rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold">N</kbd>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">{isStudio ? 'Close dialog / cancel' : 'Close dialog / Cancel'}</span>
                <kbd className="px-2.5 py-1.5 text-xs bg-slate-100 dark:bg-[#3a3d41] rounded-lg border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold">Esc</kbd>
              </div>
            </div>
            
            {/* Footer note */}
            <div className="pt-3 mt-2 border-t border-slate-100 dark:border-white/10">
              <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                On Windows, use <kbd className="px-1.5 py-0.5 text-[10px] bg-slate-100 dark:bg-[#3a3d41] rounded border border-slate-200 dark:border-white/10 font-medium">Ctrl</kbd> instead of <kbd className="px-1.5 py-0.5 text-[10px] bg-slate-100 dark:bg-[#3a3d41] rounded border border-slate-200 dark:border-white/10 font-medium">⌘</kbd>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help & Support Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="max-w-sm" data-ui-dialog="help">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span>{isStudio ? 'Help and support' : 'Help & Support'}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <a
              href="https://feedback.worshipos.app/b/features-requests"
              target="_blank"
              rel="noopener noreferrer"
              data-ui="help-row" className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-[#1e1f22] hover:bg-indigo-50 dark:hover:bg-indigo-500/15 border border-slate-100 dark:border-white/10 hover:border-indigo-200 transition-all duration-200 group"
            >
              <div data-ui="help-icon" className="w-11 h-11 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-500/30 flex items-center justify-center transition-colors">
                <Lightbulb className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100 group-hover:text-indigo-700 transition-colors">{isStudio ? 'Request a feature' : 'Request a Feature'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Share your ideas with us</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors" />
            </a>
            
            <a
              href="https://feedback.worshipos.app/b/bugs"
              target="_blank"
              rel="noopener noreferrer"
              data-ui="help-row" className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-[#1e1f22] hover:bg-amber-50 dark:hover:bg-amber-500/15 border border-slate-100 dark:border-white/10 hover:border-amber-200 transition-all duration-200 group"
            >
              <div data-ui="help-icon" className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-500/20 group-hover:bg-amber-200 dark:group-hover:bg-amber-500/30 flex items-center justify-center transition-colors">
                <Bug className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100 group-hover:text-amber-700 transition-colors">{isStudio ? 'Report a bug' : 'Report a Bug'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Help us fix issues</p>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-amber-400 transition-colors" />
            </a>
            
            <a
              href="mailto:support@worshipos.app"
              data-ui="help-row" className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-[#1e1f22] hover:bg-green-50 dark:hover:bg-green-500/15 border border-slate-100 dark:border-white/10 hover:border-green-200 transition-all duration-200 group"
            >
              <div data-ui="help-icon" className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-500/20 group-hover:bg-green-200 dark:group-hover:bg-green-500/30 flex items-center justify-center transition-colors">
                <Mail className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100 group-hover:text-green-700 transition-colors">{isStudio ? 'Email support' : 'Email Support'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">support@worshipos.app</p>
              </div>
            </a>

            <button
              onClick={() => { setShowHelpDialog(false); onOpenApp('tutorials'); }}
              data-ui="help-row" className="w-full text-left flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-[#1e1f22] hover:bg-rose-50 dark:hover:bg-rose-500/15 border border-slate-100 dark:border-white/10 hover:border-rose-200 transition-all duration-200 group"
              data-testid="help-tutorial-videos-btn"
            >
              <div data-ui="help-icon" className="w-11 h-11 rounded-xl bg-rose-100 dark:bg-rose-500/20 group-hover:bg-rose-200 dark:group-hover:bg-rose-500/30 flex items-center justify-center transition-colors">
                <Play className="w-5 h-5 text-rose-600 dark:text-rose-400" fill="currentColor" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100 group-hover:text-rose-700 transition-colors">{isStudio ? 'Tutorial videos' : 'Tutorial Videos'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Full walkthrough plus every instruction video</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content - 2x3 Grid */}
      {isStudio ? (
        <>
            {/* Closing abandons the draft — reset it so a Repeat (or assignee,
                priority, sub-task…) left on in a cancelled draft cannot carry
                into the next task. A failed submit keeps the dialog open. */}
            <AddTaskDialog
              open={showAddTask}
              onOpenChange={(v) => { setShowAddTask(v); if (!v) setNewTask(INITIAL_TASK_STATE); }}
              newTask={newTask}
              setNewTask={setNewTask}
              onSubmit={addQuickTask}
              selectedDate={new Date()}
              members={sharedContext.is_admin ? sharedContext.members : []}
              currentUserId={user?.id}
            />
            <EditTaskDialog
              open={showEditTask}
              onOpenChange={setShowEditTask}
              editingTaskData={editingTaskData}
              setEditingTaskData={setEditingTaskData}
              updateTask={updateTask}
              subtasksApi={subtasksApi}
              members={sharedContext.is_admin ? sharedContext.members : []}
              currentUserId={user?.id}
            />
            <SharedTaskEditDialog
              open={!!editingSharedTask}
              onOpenChange={(v) => { if (!v) setEditingSharedTask(null); }}
              task={editingSharedTask}
              members={sharedContext.is_admin ? sharedContext.members : []}
              currentUserId={user?.id}
              onSave={updateSharedItem}
            />
          <StudioDashboardView vm={studioVm} />
        </>
      ) : (
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className={`dashboard-grid ${gridSettled ? 'dashboard-grid-settled' : ''} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}>
          
          {/* 1. Planner Widget - Today's Tasks */}
          <Card className="card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60 min-h-[280px] flex flex-col group">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-2.5 cursor-pointer group/title"
                  onClick={() => onOpenApp('planner', 'day')}
                >
                  <div data-tile="planner" className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-md shadow-indigo-500/25 group-hover/title:shadow-lg group-hover/title:shadow-indigo-500/30 transition-all">
                    <CalendarIcon className="w-4 h-4 text-white" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Planner</CardTitle>
                </div>
                {isStudio ? (
                  <div data-ui="segmented" className="flex gap-0.5 bg-muted rounded-lg p-0.5">
                    {[['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']].map(([view, label]) => (
                      <Button
                        key={view}
                        variant="ghost"
                        size="sm"
                        data-active={view === 'day' ? 'true' : undefined}
                        className="text-[12.5px] h-7 px-2.5 rounded-md font-medium"
                        onClick={() => onOpenApp('planner', view)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                ) : (
                <div className="flex gap-1 bg-slate-100 dark:bg-[#3a3d41] rounded-lg p-0.5">
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-white/10 hover:text-indigo-600 hover:shadow-sm transition-all" onClick={() => onOpenApp('planner', 'year')}>YR</Button>
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-white/10 hover:text-indigo-600 hover:shadow-sm transition-all" onClick={() => onOpenApp('planner', 'month')}>MTH</Button>
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-white/10 hover:text-indigo-600 hover:shadow-sm transition-all" onClick={() => onOpenApp('planner', 'week')}>WK</Button>
                  <Button variant="ghost" size="sm" className="text-xs h-7 px-2.5 rounded-md bg-white dark:bg-white/10 text-indigo-600 dark:text-indigo-300 shadow-sm font-medium" onClick={() => onOpenApp('planner', 'day')}>DAY</Button>
                </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className={isStudio ? 'text-[12.5px] font-medium text-muted-foreground' : 'text-sm font-medium text-slate-600 dark:text-slate-300'}>{format(new Date(), 'EEEE, MMMM d')}</span>
                <Button size="sm" variant={isStudio ? 'ghost' : 'outline'} className={isStudio ? 'h-7 text-xs px-2.5 font-medium text-muted-foreground' : 'h-7 text-xs px-3 rounded-lg border-slate-200 dark:border-white/10 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 hover:text-indigo-600 dark:hover:text-indigo-300 transition-all'} onClick={() => setShowAddTask(true)} data-testid="dashboard-add-task-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> {isStudio ? 'Add task' : 'Add Task'}
                </Button>
                {/* Same draft reset as the Studio branch above. */}
                <AddTaskDialog
                  open={showAddTask}
                  onOpenChange={(v) => { setShowAddTask(v); if (!v) setNewTask(INITIAL_TASK_STATE); }}
                  newTask={newTask}
                  setNewTask={setNewTask}
                  onSubmit={addQuickTask}
                  selectedDate={new Date()}
                  members={sharedContext.is_admin ? sharedContext.members : []}
                  currentUserId={user?.id}
                />
                <EditTaskDialog
                  open={showEditTask}
                  onOpenChange={setShowEditTask}
                  editingTaskData={editingTaskData}
                  setEditingTaskData={setEditingTaskData}
                  updateTask={updateTask}
                  subtasksApi={subtasksApi}
                  members={sharedContext.is_admin ? sharedContext.members : []}
                  currentUserId={user?.id}
                />
                <SharedTaskEditDialog
                  open={!!editingSharedTask}
                  onOpenChange={(v) => { if (!v) setEditingSharedTask(null); }}
                  task={editingSharedTask}
                  members={sharedContext.is_admin ? sharedContext.members : []}
                  currentUserId={user?.id}
                  onSave={updateSharedItem}
                />
              </div>
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col">
              <div data-ui={isStudio ? 'task-list' : undefined} className="space-y-2 flex-1 min-h-0 max-h-64 overflow-y-auto pr-1" data-testid="dashboard-task-list">
                {/* Shared tasks scroll together with personal tasks */}
                <SharedTasksSection
                  tasks={mySharedTasks}
                  currentUserId={user?.id}
                  onToggle={toggleSharedItem}
                  onDelete={deleteSharedItem}
                  onEdit={setEditingSharedTask}
                  highlightId={highlightSharedId}
                  compact
                  setHoveredItem={setHoveredItem}
                  isOwner={sharedContext.role === 'owner'}
                />
                {/* Show uncompleted tasks + the one being completed (for animation) */}
                {todayTasks.filter(t => !t.completed || t.id === completingTaskId).length > 0 ? 
                  todayTasks
                    .filter(t => !t.completed || t.id === completingTaskId)
                    .map((task) => {
                    const st = subtasksApi.getForTask(task);
                    return (
                  <React.Fragment key={task.id}>
                  <div 
                    data-ui={isStudio ? 'task-row' : undefined}
                    className={`${isStudio
                      ? 'flex items-center gap-2.5 cursor-pointer transition-colors'
                      : 'flex items-center gap-3 p-3 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer border border-transparent hover:border-slate-200 transition-all duration-300'} ${
                      completingTaskId === task.id ? 'opacity-50' : ''
                    }`}
                    onClick={() => handleEditTask(task)}
                    onMouseEnter={() => setHoveredItem && setHoveredItem({ type: 'task', id: task.id, data: task })}
                    onMouseLeave={() => setHoveredItem && setHoveredItem(null)}
                    data-testid={`dashboard-task-${task.id}`}
                  >
                    <PriorityCheckbox 
                      priority={task.priority}
                      checked={task.completed || completingTaskId === task.id} 
                      className="transition-all" 
                      onCheckedChange={() => toggleTask(task.id, task.completed, { title: task.title })}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <span className={`${isStudio ? 'text-[14px] font-normal' : 'text-sm font-medium'} transition-all duration-300 truncate ${
                        completingTaskId === task.id ? 'task-strike text-slate-400 dark:text-slate-500' : (isStudio ? 'text-foreground' : 'text-slate-700 dark:text-slate-200')
                      }`}>{task.title}</span>
                      {st.items.length > 0 && (
                        <SubtaskProgressChip
                          done={st.checkedIds.length}
                          total={st.items.length}
                          expanded={expandedSubtasks.has(task.id)}
                          onClick={() => toggleSubtaskExpand(task.id)}
                        />
                      )}
                    </div>
                    {task.time && <span className={isStudio ? 'text-[12.5px] text-slate-400 dark:text-slate-500 tabular-nums' : 'text-xs text-slate-400 dark:text-slate-500 bg-card px-2 py-0.5 rounded-md'}>{formatTime12h(task.time)}</span>}
                  </div>
                  {expandedSubtasks.has(task.id) && st.items.length > 0 && (
                    <SubtaskChecklist
                      items={st.items}
                      checkedIds={st.checkedIds}
                      onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
                    />
                  )}
                  </React.Fragment>
                    );
                  }) : (
                  <div className={isStudio ? 'studio-empty' : 'text-center py-10 text-slate-400 dark:text-slate-500'}>
                    <div className={isStudio ? 'studio-empty-glyph' : 'w-14 h-14 rounded-2xl bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mx-auto mb-3'}>
                      <CalendarIcon className={isStudio ? 'w-5 h-5' : 'w-7 h-7 text-slate-300'} />
                    </div>
                    <p className={isStudio ? 'text-[15px] font-semibold text-foreground' : 'text-sm font-medium text-slate-500 dark:text-slate-400'}>
                      {todayTasks.filter(t => t.completed).length > 0 ? 'All tasks completed! 🎉' : 'No tasks for today'}
                    </p>
                    <p className={isStudio ? 'text-[13.5px] text-muted-foreground' : 'text-xs text-slate-400 dark:text-slate-500 mt-1'}>
                      {todayTasks.filter(t => t.completed).length > 0 ? 'Great job!' : (isStudio ? 'Click "Add task" to get started.' : 'Click "Add Task" to get started')}
                    </p>
                  </div>
                )}
              </div>
              <Button data-ui="open-link" variant="ghost" className="w-full mt-3 text-indigo-600 dark:text-indigo-400 text-sm h-10 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-xl font-medium transition-all" onClick={() => onOpenApp('planner', 'day')}>
                {isStudio ? 'Open planner' : 'Open Full Planner →'}
              </Button>
            </CardContent>
          </Card>

          {/* 2. Team Comms Widget */}
          <Card className="card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60 min-h-[280px] flex flex-col group">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-2.5 cursor-pointer group/title"
                  onClick={() => onOpenApp('communication')}
                >
                  <div data-tile="comms" className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-500/25 group-hover/title:shadow-lg group-hover/title:shadow-blue-500/30 transition-all">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Team Comms</CardTitle>
                </div>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-500/15 hover:text-blue-600 transition-all" onClick={() => setShowQuickCompose(true)} data-testid="quick-compose-trigger">
                        <Mail className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                      <p>Quick Message <span className="opacity-70 ml-1">(Shift+M)</span></p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col">
              <div className="space-y-2 flex-1">
                {recentComms.length > 0 ? recentComms.map((item) => (
                  <div 
                    key={`${item.type}-${item.id}`} 
                    className="flex gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl p-3 -mx-1 transition-all border border-transparent hover:border-slate-200" 
                    onClick={() => item.type === 'message' 
                      ? onOpenApp('comms', 'messages', item.conversationId, null, true, null, item.id) 
                      : onOpenApp('comms', 'feed', item.id)
                    }
                  >
                    <UserAvatar 
                      userId={item.author?.id || item.user_id}
                      name={item.author?.name}
                      avatarUrl={item.type === 'post' ? (item.author?.avatar_url || item.author?.avatar) : (getCachedAvatar(item.author?.id) || item.author?.avatar)}
                      size={isStudio ? 'sm' : 'md'}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-800 dark:text-slate-100">{item.author?.name || 'Unknown'}</span>
                        {isStudio ? (
                          /* the mock carries the kind as meta text, not a coloured chip */
                          <span className="text-[12.5px] text-slate-400 dark:text-slate-500">
                            {item.type === 'message' ? 'Message' : 'Post'} · {formatTimeAgo(item.created_at)}
                          </span>
                        ) : (
                          <>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          item.type === 'message' 
                            ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' 
                            : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                        }`}>
                          {item.type === 'message' ? 'Message' : 'Post'}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">• {formatTimeAgo(item.created_at)}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5">
                        {stripHtml(item.content) || (item.embed?.video_id ? '▶️ Video' : item.media_urls?.length ? '📷 Image' : item.voice_memo_data ? '🎤 Voice memo' : item.gif_url ? '🎬 GIF' : 'No content')}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div className="space-y-2">
                    <div className="flex gap-3 rounded-xl p-3 -mx-1">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">SP</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-slate-400 dark:text-slate-500">Sample Post</span>
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-400">Post</span>
                        </div>
                        <p className="text-sm text-slate-400 dark:text-slate-500 mt-0.5">Latest posts will appear here</p>
                      </div>
                    </div>
                    <div className="flex gap-3 rounded-xl p-3 -mx-1">
                      <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">SM</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-slate-400 dark:text-slate-500">Sample Message</span>
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950/40 text-purple-400">Message</span>
                        </div>
                        <p className="text-sm text-slate-400 dark:text-slate-500 mt-0.5">Latest messages will appear here</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Button data-ui="open-link" variant="ghost" className="w-full mt-2 text-blue-600 dark:text-blue-400 text-sm h-9 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-xl font-medium transition-all" onClick={() => onOpenApp('comms')}>
                {isStudio ? 'Open Team Comms' : 'Open Team Comms →'}
              </Button>
            </CardContent>
          </Card>

          {/* 3. Worship AI Widget */}
          <Card data-testid="worship-ai-widget" className="card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60">
            <CardHeader className="pb-5">
              <div 
                className="flex items-center gap-2.5 cursor-pointer group/title"
                onClick={() => onOpenApp('aichat')}
              >
                <div data-tile="ai" className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md shadow-amber-500/25 group-hover/title:shadow-lg group-hover/title:shadow-amber-500/30 transition-all">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Worship AI</CardTitle>
                {!isStudio && (
                  <Badge className="bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-500/20 dark:to-orange-500/20 text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 font-medium border-0">BETA</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {isStudio ? (
                <p className="text-sm text-muted-foreground mb-3">Need help with setlists or solving a problem?</p>
              ) : (
              <div className="flex gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/25">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] rounded-xl rounded-tl-sm px-3 py-2 flex-1 border border-slate-100 dark:border-white/10">
                  <p className="text-sm text-slate-600 dark:text-slate-300">Need help with setlists or solving a problem?</p>
                </div>
              </div>
              )}
              <div className={isStudio ? 'flex gap-1.5 mb-3' : 'flex gap-2 mb-3'}>
                <Button data-testid="setlist-ideas-btn" variant="outline" size="sm" className={isStudio ? 'studio-chip h-6 flex-none' : "text-xs h-9 flex-1 rounded-lg border-slate-200 dark:border-white/10 hover:border-amber-300 dark:hover:border-amber-500/40 hover:bg-amber-50 dark:hover:bg-amber-500/15 hover:text-amber-700 dark:hover:text-amber-200 transition-all"} onClick={() => onOpenApp('aichat', null, 'I want help finding songs and calls to worship for my pastors sermon on [ENTER PASSAGE OR THEME HERE]', null, false)}>
                  <Music className="w-3.5 h-3.5 mr-1.5" /> {isStudio ? 'Setlist helper' : 'Setlist Helper'}
                </Button>
                <Button data-testid="team-email-btn" variant="outline" size="sm" className={isStudio ? 'studio-chip h-6 flex-none' : "text-xs h-9 flex-1 rounded-lg border-slate-200 dark:border-white/10 hover:border-amber-300 dark:hover:border-amber-500/40 hover:bg-amber-50 dark:hover:bg-amber-500/15 hover:text-amber-700 dark:hover:text-amber-200 transition-all"} onClick={() => onOpenApp('aichat', null, 'I am having a problem in my ministry and need help solving it. My problem is this [DESCRIBE PROBLEM IN AS MUCH DETAIL AS YOU CAN]', null, false)}>
                  <Lightbulb className="w-3.5 h-3.5 mr-1.5" /> {isStudio ? 'Problem solver' : 'Problem Solver'}
                </Button>
              </div>
              <div className={isStudio ? 'flex items-center gap-2 h-10 pl-3 pr-1.5 rounded-lg border border-input bg-card' : 'flex gap-2'} data-ui={isStudio ? 'ask-field' : undefined}>
                <Input data-testid="ai-quick-input" placeholder="Ask anything..." value={aiMessage} onChange={(e) => setAiMessage(e.target.value)} className={isStudio ? 'flex-1 h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus:shadow-none' : "h-10 text-sm rounded-lg bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10 focus:bg-white dark:focus:bg-[#2d2f33] focus:border-amber-400 transition-all"} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && aiMessage.trim()) { e.preventDefault(); onOpenApp('aichat', null, aiMessage.trim()); setAiMessage(''); } }} />
                <Button data-testid="ai-quick-send-btn" size="sm" className={isStudio
                  ? 'h-7 w-7 p-0 rounded-md flex-none'
                  : 'h-10 w-10 p-0 bg-gradient-to-br from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-lg shadow-md shadow-amber-500/25 hover:shadow-lg hover:shadow-amber-500/30 transition-all'} onClick={() => { if (aiMessage.trim()) { onOpenApp('aichat', null, aiMessage.trim()); setAiMessage(''); } else { onOpenApp('aichat'); } }}>
                  <Send className={isStudio ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                </Button>
              </div>
              {isStudio && (
                <Button data-ui="open-link" variant="ghost" className="w-full mt-3 text-sm h-8 font-medium" onClick={() => onOpenApp('aichat')}>
                  Open Worship AI
                </Button>
              )}
            </CardContent>
          </Card>

          {/* 4. Team Building Widget */}
          <Card className="card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2.5">
                <div data-tile="team" className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-md shadow-rose-500/25">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Team Building</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Team Actions */}
              {isStudio ? (
                <div className="mb-4">
                  <div className="text-[12.5px] font-medium text-muted-foreground mb-1">Team action this week</div>
                  <div className="space-y-1">
                    {currentWeekContent.actions.map((action, idx) => (
                      <p key={idx} className="text-[14px] text-foreground leading-snug">{action}</p>
                    ))}
                  </div>
                </div>
              ) : (
              <div className="p-3 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-500/15 dark:to-pink-500/15 rounded-xl mb-3 border border-rose-100 dark:border-rose-500/30">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-300 uppercase mb-2 tracking-wide">
                  <Users className="w-3.5 h-3.5" /> Team Actions This Week
                </div>
                <div className="space-y-1.5">
                  {currentWeekContent.actions.map((action, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 flex-shrink-0"></div>
                      <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
              )}
              
              {/* Featured Workshop strip — leaders only (R12) */}
              {isAdmin && user?.church_slug && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => spaNavigate(`/${user.church_slug}/training/workshop`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      spaNavigate(`/${user.church_slug}/training/workshop`);
                    }
                  }}
                  className={isStudio
                    ? 'mb-3 p-2.5 rounded-md border border-border bg-card flex items-center gap-3 cursor-pointer hover:bg-muted transition-colors focus:outline-none studio-focus'
                    : 'mb-3 p-3 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-gradient-to-br from-rose-50/70 to-pink-50/70 dark:from-rose-500/10 dark:to-pink-500/10 flex items-center gap-3 cursor-pointer hover:from-rose-100 hover:to-pink-100 dark:hover:from-rose-500/15 dark:hover:to-pink-500/15 transition-all focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900'}
                  data-testid="featured-workshop-strip"
                >
                  {featuredWorkshop ? (
                    <>
                      <div className={isStudio ? 'relative w-[76px] h-12 rounded-md overflow-hidden flex-shrink-0' : 'relative w-16 h-11 rounded-lg overflow-hidden flex-shrink-0 shadow-md shadow-rose-400/30'}>
                        <img src={featuredWorkshop.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                          <Play className="w-4 h-4 text-white" fill="currentColor" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={isStudio
                          ? 'text-[11.5px] font-medium text-muted-foreground'
                          : 'text-[10px] font-semibold text-rose-600 dark:text-rose-300 uppercase tracking-wide'}>
                          {featuredWorkshop.quarter_label || 'This Quarter'} · {isStudio ? 'Team workshop' : 'Team Workshop'}
                        </div>
                        <div className={isStudio ? 'text-[13.5px] font-semibold text-foreground truncate' : 'text-sm font-medium text-slate-700 dark:text-slate-200 truncate'}>{featuredWorkshop.title}</div>
                        {featuredWorkshop.description && (
                          <div className={isStudio ? 'text-[12.5px] text-muted-foreground truncate' : 'text-xs text-slate-500 dark:text-slate-400 truncate'}>{featuredWorkshop.description}</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={isStudio
                        ? 'w-11 h-11 rounded-lg bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0'
                        : 'w-11 h-11 rounded-lg bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-rose-400/30'}>
                        <GraduationCap className={isStudio ? 'w-5 h-5' : 'w-5 h-5 text-white'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={isStudio
                          ? 'text-[11.5px] font-medium text-muted-foreground'
                          : 'text-[10px] font-semibold text-rose-600 dark:text-rose-300 uppercase tracking-wide'}>{isStudio ? 'Coming soon' : 'Coming Soon'}</div>
                        <div className={isStudio ? 'text-[13.5px] font-semibold text-foreground truncate' : 'text-sm font-medium text-slate-700 dark:text-slate-200 truncate'}>{isStudio ? 'Quarterly team workshop' : 'Quarterly Team Workshop'}</div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Devo Download Button */}
              <Button 
                variant={isStudio ? 'outline' : 'default'}
                className={isStudio
                  ? 'w-full h-9 rounded-lg font-medium text-sm'
                  : 'w-full h-10 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg shadow-rose-500/25 hover:shadow-xl hover:shadow-rose-500/30 transition-all font-medium text-sm'}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(currentWeekContent.pdfUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                {isStudio ? <>Download this week&apos;s devo</> : <>Download This Week&apos;s Devo</>}
              </Button>

              {/* Auto-post devos toggle + settings */}
              <div className={isStudio
                ? 'mt-3 flex items-center justify-between gap-2'
                : 'mt-3 flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10'}>
                <span className={isStudio ? 'text-[13.5px] text-foreground' : 'text-sm font-medium text-slate-600 dark:text-slate-300'}>{isStudio ? 'Auto-post devos to the team feed' : 'Auto-post devos to team feed'}</span>
                <div className={isStudio ? 'flex items-center gap-1 flex-row-reverse' : 'flex items-center gap-1'}>
                  <Switch
                    checked={!!devoSettings.enabled}
                    disabled={!devoSettingsFetched}
                    onCheckedChange={toggleDevoAutoPost}
                    className={isStudio ? undefined : 'data-[state=checked]:bg-rose-500'}
                    data-testid="devo-autopost-switch"
                  />
                  <button
                    onClick={openDevoSettings}
                    className="p-1.5 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-white/10 transition-colors"
                    title="Devotion post settings"
                    data-testid="devo-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Training Library ghost button */}
              {user?.church_slug && (
                <Button
                  variant="ghost"
                  className="w-full mt-3 text-rose-500 dark:text-rose-300 text-sm h-10 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl font-medium transition-all"
                  onMouseEnter={() => { import('../training/TrainingLibraryHome'); }}
                  onClick={() => spaNavigate(`/${user.church_slug}/training`)}
                  data-testid="open-training-library-btn"
                  data-ui="open-link"
                >
                  {isStudio ? 'Open training library' : 'Open Training Library →'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* 5. Onboarding Widget */}
          <Card className="card-lift shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-2.5 cursor-pointer group/title"
                  onClick={() => onOpenApp('onboarding')}
                >
                  <div data-tile="onb" className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shadow-emerald-500/25 group-hover/title:shadow-lg group-hover/title:shadow-emerald-500/30 transition-all">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">Onboarding</CardTitle>
                </div>
                {isStudio ? (
                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <span className="studio-count">{pendingCount}</span> pending
                  </div>
                ) : (
                <Badge className="bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-500/20 dark:to-orange-500/20 text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 font-medium border-0">
                  {pendingCount} PENDING
                </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {trackBreakdown.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pb-2 mb-1 border-b border-slate-100 dark:border-white/5" data-testid="onboarding-track-breakdown">
                    {isStudio ? (
                      <>
                        <span className="studio-chip">Main <b className="font-semibold text-foreground">{pendingCount}</b></span>
                        {trackBreakdown.map((t) => (
                          <span key={t.id} className="studio-chip">{t.name} <b className="font-semibold text-foreground">{t.candidate_count || 0}</b></span>
                        ))}
                      </>
                    ) : (
                      <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                      Main · {pendingCount}
                    </span>
                    {trackBreakdown.map((t) => (
                      <span key={t.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/15 text-[11px] font-medium text-indigo-600 dark:text-indigo-300">
                        {t.name} · {t.candidate_count || 0}
                      </span>
                    ))}
                      </>
                    )}
                  </div>
                )}
                {onboardingCandidates.length === 0 ? (
                  <div className={isStudio ? 'studio-empty' : 'text-center py-6'}>
                    <div className={isStudio ? 'studio-empty-glyph' : 'w-12 h-12 rounded-xl bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mx-auto mb-2'}>
                      <Users className={isStudio ? 'w-5 h-5' : 'w-6 h-6 text-slate-300'} />
                    </div>
                    <p className={isStudio ? 'text-[15px] font-semibold text-foreground' : 'text-sm font-medium text-slate-500 dark:text-slate-400'}>No applicants yet</p>
                    <p className={isStudio ? 'text-[13.5px] text-muted-foreground' : 'text-xs text-slate-400 dark:text-slate-500 mt-0.5'}>{isStudio ? 'Share your link to get started.' : 'Share your link to get started'}</p>
                  </div>
                ) : (
                  onboardingCandidates.slice(0, 2).map((candidate) => (
                    <div key={candidate.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-all cursor-pointer" onClick={() => onOpenApp('onboarding', candidate.id)} data-testid={`onboarding-candidate-${candidate.id}`}>
                      <UserAvatar 
                        userId={candidate.id}
                        name={candidate.name}
                        avatarUrl={candidate.avatar_url}
                        size="sm"
                        className={isStudio ? 'w-7 h-7 text-[11px]' : 'w-9 h-9'}
                      />
                      <div className="flex-1 min-w-0">
                        <span className={isStudio ? 'font-semibold text-[13.5px] text-foreground block truncate' : 'font-semibold text-sm text-slate-800 dark:text-slate-100 block truncate'}>
                          {candidate.name?.split(' ')[0] || 'Unknown'}
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-slate-100 dark:bg-[#3a3d41] rounded-full overflow-hidden">
                            <div 
                              data-stage-fill={candidate.stage}
                              className={`h-full ${STAGE_COLORS[candidate.stage] || 'bg-blue-500'} rounded-full transition-all`} 
                              style={{ width: `${STAGE_PROGRESS[candidate.stage] || 20}%` }}
                            ></div>
                          </div>
                          <Badge variant="outline" data-stage={candidate.stage} className="text-[10px] px-1.5 py-0 h-4 font-medium">
                            {isStudio
                              ? (STAGE_LABELS_STUDIO[candidate.stage] || candidate.stage)
                              : (STAGE_LABELS[candidate.stage] || candidate.stage?.toUpperCase())}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div 
                className={isStudio
                  ? 'mt-3 h-10 px-3 rounded-lg flex items-center gap-2.5 cursor-pointer hover:bg-muted transition-colors border border-dashed border-input'
                  : 'mt-3 p-3 bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] rounded-xl flex items-center gap-2.5 cursor-pointer hover:from-slate-100 hover:to-slate-150 transition-all border border-slate-100 dark:border-white/10'}
                onClick={copyIntakeLink}
              >
                {!isStudio && (
                <div className="w-8 h-8 rounded-lg bg-card shadow-sm flex items-center justify-center">
                  <QrCode className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                </div>
                )}
                {isStudio && <QrCode className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                <div className="flex-1">
                  <div className={isStudio ? 'text-[13.5px] font-medium text-foreground' : 'text-sm font-medium text-slate-700 dark:text-slate-200'}>
                    {copiedLink ? 'Link copied!' : (isStudio ? 'Share application' : 'Share Application')}
                  </div>
                </div>
                {copiedLink ? (
                  <span className="text-green-500 text-sm font-medium like-pop">✓</span>
                ) : (
                  <Copy className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                )}
              </div>
              {isStudio && (
                <Button data-ui="open-link" variant="ghost" className="w-full mt-3 text-sm h-8 font-medium" onClick={() => onOpenApp('onboarding')}>
                  Open onboarding
                </Button>
              )}
            </CardContent>
          </Card>

          {/* 6. Upcoming Services: Planning Center widget, Simple Planner widget, or chooser */}
          {planningCenterLoading || servicesModeLoading ? (
            /* Show skeleton while loading (only when no cached status) */
            <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card animate-pulse">
              <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-[#3e4145]" />
                  <div className="h-5 w-28 bg-slate-200 dark:bg-[#3e4145] rounded" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-slate-100 dark:bg-[#3a3d41] rounded" />
                  <div className="h-4 w-3/4 bg-slate-100 dark:bg-[#3a3d41] rounded" />
                  <div className="h-4 w-1/2 bg-slate-100 dark:bg-[#3a3d41] rounded" />
                </div>
              </CardContent>
            </Card>
          ) : planningCenterConnected ? (
            /* When Planning Center is connected: PC widget fills the slot */
            <PlanningCenterWidget 
              userId={user?.id} 
              onReconnect={connectPlanningCenter}
              onQuickMessage={(servicePlan) => {
                setPcoServicePlan(servicePlan);
                setShowQuickCompose(true);
              }}
            />
          ) : servicesMode !== 'off' ? (
            <ServicesWidget user={user} mode={servicesMode} onOpenApp={onOpenApp} onConnectPlanningCenter={connectPlanningCenter} onQuickMessage={(servicePlan) => { setPcoServicePlan(servicePlan); setShowQuickCompose(true); }} />
          ) : (
            /* Default: Quick Stats + Quick Links side by side */
            isStudio ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Quick stats — neutral card (101 §1: no gradient, no second accent) */}
              <Card className="border border-border bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2.5 mb-4">
                    <div data-tile="pc" className="w-8 h-8 rounded-lg flex items-center justify-center">
                      <LayoutDashboard className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[16px] font-semibold text-foreground">Quick stats</span>
                  </div>
                  <div className="grid grid-cols-2 gap-y-3.5 gap-x-4">
                    <div>
                      <div className="text-2xl font-semibold tabular-nums text-foreground"><CountUp value={todayTasks.filter(t => !t.completed).length} /></div>
                      <div className="text-[12.5px] text-muted-foreground">Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-semibold tabular-nums text-foreground"><CountUp value={todayTasks.filter(t => t.completed).length} /></div>
                      <div className="text-[12.5px] text-muted-foreground">Completed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-semibold tabular-nums text-foreground"><CountUp value={pendingCount} /></div>
                      <div className="text-[12.5px] text-muted-foreground">Onboarding</div>
                    </div>
                    <div
                      onClick={() => onOpenApp('messaging')}
                      className="cursor-pointer rounded-lg -m-1 p-1 hover:bg-muted transition-colors"
                    >
                      <div className="text-2xl font-semibold tabular-nums text-foreground"><CountUp value={teamCommsNotifications.filter(n => !n.is_read && n.sender_id !== user?.id).length} /></div>
                      <div className="text-[12.5px] text-muted-foreground">Messages</div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => onOpenSettings('team')}
                    className="w-full mt-5 h-8 rounded-lg text-[13px] font-medium gap-2"
                    data-testid="invite-team-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                    Invite team
                  </Button>
                </CardContent>
              </Card>

              {/* Quick links — neutral card */}
              <Card className="border border-border bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div data-tile="pc" className="w-8 h-8 rounded-lg flex items-center justify-center">
                        <Globe className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-[16px] font-semibold text-foreground">Quick links</span>
                    </div>
                    <button
                      onClick={() => setShowAddLinkDialog(true)}
                      className="w-7 h-7 grid place-items-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
                      title="Manage links"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {quickLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center h-9 justify-between px-2.5 rounded-lg border border-border hover:bg-muted transition-colors group"
                      >
                        <span className="text-[13px] font-medium text-foreground truncate pr-2">{link.title}</span>
                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                      </a>
                    ))}
                    {quickLinks.length < 5 && (
                      <button
                        onClick={() => setShowAddLinkDialog(true)}
                        className="flex items-center justify-start gap-1.5 w-full h-8 px-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-[13px] font-medium"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add link
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            ) : (
            <div className="grid grid-cols-2 gap-6">
              {/* Quick Stats Widget - Half width */}
              <Card className="card-lift shadow-xl shadow-indigo-500/10 border-0 bg-gradient-to-br from-indigo-600 via-indigo-600 to-purple-700 text-white hover:shadow-2xl hover:shadow-indigo-500/20 overflow-hidden relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
                <CardContent className="p-5 relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center">
                      <LayoutDashboard className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-semibold text-sm">Quick Stats</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-2xl font-bold"><CountUp value={todayTasks.filter(t => !t.completed).length} /></div>
                      <div className="text-xs text-indigo-200 font-medium">Tasks</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold"><CountUp value={todayTasks.filter(t => t.completed).length} /></div>
                      <div className="text-xs text-indigo-200 font-medium">Completed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold"><CountUp value={pendingCount} /></div>
                      <div className="text-xs text-indigo-200 font-medium">Onboarding</div>
                    </div>
                    <div 
                      onClick={() => onOpenApp('messaging')}
                      className="cursor-pointer hover:bg-white/10 rounded-lg p-1 -m-1 transition-all"
                    >
                      <div className="text-2xl font-bold"><CountUp value={teamCommsNotifications.filter(n => !n.is_read && n.sender_id !== user?.id).length} /></div>
                      <div className="text-xs text-indigo-200 font-medium">Messages</div>
                    </div>
                  </div>
                  
                  {/* Invite Team Members Button */}
                  <Button
                    onClick={() => onOpenSettings('team')}
                    className="invite-team-btn w-full mt-6 bg-white/90 dark:bg-[#2d2f33]/90 hover:bg-white dark:hover:bg-white/10 text-indigo-700 dark:text-indigo-300 border-0 font-semibold text-sm h-9 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-900/20 hover:shadow-xl hover:shadow-indigo-900/30 overflow-hidden relative"
                    data-testid="invite-team-btn"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent -translate-x-full animate-[shine_45s_ease-in-out_infinite]"></span>
                    <UserPlus className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Invite Team</span>
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Links Widget - Half width */}
              <Card className="card-lift shadow-xl shadow-slate-500/10 border-0 bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 text-white hover:shadow-2xl hover:shadow-slate-500/20 overflow-hidden relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
                <CardContent className="p-5 relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center">
                        <Globe className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-semibold text-sm">Quick Links</span>
                    </div>
                    <button
                      onClick={() => setShowAddLinkDialog(true)}
                      className="p-1.5 hover:bg-white/10 rounded-md transition-all"
                      title="Manage links"
                    >
                      <Settings className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {quickLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between py-2 px-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-all group"
                      >
                        <span className="text-xs font-medium truncate pr-2">{link.title}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                    {quickLinks.length < 5 && (
                      <button
                        onClick={() => setShowAddLinkDialog(true)}
                        className="flex items-center justify-center gap-1.5 w-full py-2 px-2.5 rounded-lg border border-dashed border-white/20 text-white/50 hover:text-white/80 hover:border-white/40 hover:bg-white/5 transition-all text-xs font-medium"
                      >
                        <Plus className="w-3 h-3" />
                        Add link
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            )
          )}

          {/* Edit/Add Quick Links Dialog */}
          <Dialog open={showAddLinkDialog} onOpenChange={(open) => {
            setShowAddLinkDialog(open);
            if (!open) {
              setNewLinkTitle('');
              setNewLinkUrl('');
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader className="pb-2">
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span>{isStudio ? 'Manage quick links' : 'Manage Quick Links'}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Existing Links - Editable */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">{isStudio ? 'Your links' : 'Your Links'}</Label>
                  {quickLinks.map((link, index) => (
                    <div key={link.id} className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-[#1e1f22] rounded-xl">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={link.title}
                          onChange={(e) => {
                            const updated = [...quickLinks];
                            updated[index] = { ...link, title: e.target.value };
                            setQuickLinks(updated);
                          }}
                          onBlur={() => saveQuickLinks(quickLinks)}
                          placeholder="Title"
                          className="h-10 text-sm"
                        />
                        <Input
                          value={link.url}
                          onChange={(e) => {
                            const updated = [...quickLinks];
                            updated[index] = { ...link, url: e.target.value };
                            setQuickLinks(updated);
                          }}
                          onBlur={() => saveQuickLinks(quickLinks)}
                          placeholder="https://..."
                          className="h-10 text-sm text-slate-500 dark:text-slate-400"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 dark:text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/15 h-10 w-10 p-0 rounded-lg"
                        onClick={() => {
                          const updated = quickLinks.filter((_, i) => i !== index);
                          setQuickLinks(updated);
                          saveQuickLinks(updated); // Auto-save on delete
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Add New Link */}
                <div className="border-t border-slate-100 dark:border-white/10 pt-4">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3 block">Add New Link</Label>
                  <div className="space-y-2">
                    <Input
                      placeholder="Title (e.g., My Favorite Tool)"
                      value={newLinkTitle}
                      onChange={(e) => setNewLinkTitle(e.target.value)}
                      className="h-11"
                      data-testid="new-link-title"
                    />
                    <Input
                      placeholder="https://example.com"
                      value={newLinkUrl}
                      onChange={(e) => setNewLinkUrl(e.target.value)}
                      className="h-11"
                      data-testid="new-link-url"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => {
                  setShowAddLinkDialog(false);
                  setNewLinkTitle('');
                  setNewLinkUrl('');
                }}>
                  Cancel
                </Button>
                <Button 
                  className="bg-indigo-500 hover:bg-indigo-600"
                  onClick={() => {
                    // If there's a new link to add, add it first
                    let linksToSave = [...quickLinks];
                    if (newLinkTitle.trim() && newLinkUrl.trim()) {
                      const newLink = {
                        id: Date.now().toString(),
                        title: newLinkTitle.trim(),
                        url: newLinkUrl.trim().startsWith('http') ? newLinkUrl.trim() : `https://${newLinkUrl.trim()}`
                      };
                      linksToSave = [...linksToSave, newLink];
                      setQuickLinks(linksToSave);
                    }
                    saveQuickLinks(linksToSave);
                    setNewLinkTitle('');
                    setNewLinkUrl('');
                    setShowAddLinkDialog(false);
                  }}
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </div>

        {/* Verse of the Day - below widgets */}
        {verseOfTheDay && (
          isStudio ? (
          <div className="verse-in max-w-[640px] mx-auto mt-[34px] text-center">
            <p className="text-[13.5px] italic leading-[1.55] text-muted-foreground text-balance">
              &ldquo;{verseOfTheDay.verse.replace(/\n/g, ' ')}&rdquo;
            </p>
            <p className="mt-1 text-[12.5px] font-medium text-slate-400 dark:text-slate-500">{verseOfTheDay.reference}</p>
          </div>
          ) : (
          <div className="verse-in flex items-center justify-center gap-2 px-5 pt-6 pb-2 max-w-xl mx-auto">
            <BookOpen className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <p className="text-[13px] text-slate-400 dark:text-slate-500 italic leading-snug text-center">
              &quot;{verseOfTheDay.verse.replace(/\n/g, ' ')}&quot;
              <span className="not-italic ml-1.5 text-slate-400/70">— {verseOfTheDay.reference}</span>
            </p>
          </div>
          )
        )}

      </main>
      )}

      {/* Quick Links Bar - Only shown when Planning Center is connected */}
      {(planningCenterConnected || servicesMode !== 'off') && (
        <div className="max-w-7xl mx-auto px-6 pt-0 pb-8 flex justify-center">
          <Card className="shadow-sm border border-slate-200 dark:border-white/10 bg-card inline-block">
            <CardContent className="py-3 px-5">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {quickLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 transition-all text-xs"
                  >
                    {link.title}
                    <ExternalLink className="w-2.5 h-2.5 opacity-40" />
                  </a>
                ))}
                <button
                  onClick={() => setShowAddLinkDialog(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 transition-all text-xs"
                >
                  <Plus className="w-3 h-3" />
                  <span>Add link</span>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>

      {/* Quick Compose Modal — rendered at App.js level (lifted state) so the
          global Shift+M shortcut can open it from any route. */}

      {/* Download App Dialog */}
      <Dialog open={showDownloadApp} onOpenChange={setShowDownloadApp}>
        <DialogContent className="sm:max-w-lg">
          <div className="py-4">
            <DownloadAppContent variant="dialog" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Devo Settings Modal */}
      {showDevoSettings && (
        <StudioOrPlainModal
          isStudio={isStudio}
          onClose={() => setShowDevoSettings(false)}
          className={`${isStudio ? 'max-w-md' : 'max-w-sm'} p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col`}
        >
            <div className={isStudio
              ? 'px-5 pt-4 pb-3 flex-shrink-0'
              : 'px-6 py-4 border-b border-slate-100 dark:border-white/10 flex-shrink-0'}>
              <h3 className={isStudio ? 'text-[17px] font-semibold tracking-tight text-foreground' : 'text-lg font-semibold text-slate-800 dark:text-slate-100'}>{isStudio ? 'Devotion post settings' : 'Devotion Post Settings'}</h3>
              <p className={isStudio ? 'text-[13.5px] text-muted-foreground mt-1' : 'text-sm text-slate-500 dark:text-slate-400 mt-0.5'}>Configure when devotions are posted to your team feed{isStudio ? '.' : ''}</p>
            </div>
            
            {devoSettingsLoading ? (
              <div className="p-8 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-indigo-300 dark:border-indigo-800 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            ) : (
              <div className={isStudio ? 'px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0' : 'p-6 space-y-5 overflow-y-auto flex-1 min-h-0'}>
                {/* Enable/Disable */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Auto-post devotions</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Automatically post weekly devotions to team feed</div>
                  </div>
                  {isStudio ? (
                    <Switch
                      checked={!!devoSettings.enabled}
                      onCheckedChange={() => setDevoSettings(s => ({ ...s, enabled: !s.enabled }))}
                      data-testid="devo-enabled-toggle"
                    />
                  ) : (
                  <button
                    onClick={() => setDevoSettings(s => ({ ...s, enabled: !s.enabled }))}
                    className={`relative rounded-full transition-colors ${devoSettings.enabled ? 'bg-indigo-600' : 'bg-slate-300'}`}
                    style={{ width: 40, height: 22 }}
                    data-testid="devo-enabled-toggle"
                  >
                    <span className={`absolute bg-card rounded-full shadow transition-transform ${devoSettings.enabled ? 'translate-x-[18px]' : ''}`} style={{ width: 18, height: 18, top: 2, left: 2 }} />
                  </button>
                  )}
                </div>

                {/* Day of Week */}
                {devoSettings.enabled && (
                  <>
                    <div>
                      <label className={isStudio ? 'block text-[13px] font-medium text-foreground mb-2' : 'block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2'}>Post day</label>
                      <div className="grid grid-cols-7 gap-1">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                          <button
                            key={day}
                            onClick={() => setDevoSettings(s => ({ ...s, post_day: day }))}
                            className={isStudio
                              ? `studio-daybtn rounded-lg text-[12.5px] font-medium transition-colors border ${
                                  devoSettings.post_day === day
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'text-muted-foreground hover:bg-muted'
                                }`
                              : `py-2 rounded-lg text-xs font-medium transition-colors ${
                              devoSettings.post_day === day
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                            }`}
                            data-testid={`devo-day-${day}`}
                          >
                            {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Week Selection */}
                    <div>
                      <label className={isStudio ? 'block text-[13px] font-medium text-foreground mb-1' : 'block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1'}>Current devo week</label>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">Choose which week your team starts on</p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setDevoSettings(s => {
                            const curr = s.current_week || backendDevoData?.week_number || 1;
                            return { ...s, current_week: Math.max(1, curr - 1) };
                          })}
                          className={isStudio
                            ? 'w-7 h-7 rounded-lg border border-input bg-card hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors'
                            : 'w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 flex items-center justify-center text-slate-600 dark:text-slate-300 font-bold transition-colors'}
                        >
                          -
                        </button>
                        <div className="flex items-baseline gap-1">
                          <span className={isStudio ? 'text-[22px] font-semibold text-foreground tabular-nums' : 'text-2xl font-bold text-indigo-600 dark:text-indigo-400'}>{devoSettings.current_week || backendDevoData?.week_number || 1}</span>
                          <span className={isStudio ? 'text-sm text-muted-foreground' : 'text-sm text-slate-400 dark:text-slate-500'}>of {backendDevoData?.total_weeks || 52}</span>
                        </div>
                        <button
                          onClick={() => setDevoSettings(s => {
                            const curr = s.current_week || backendDevoData?.week_number || 0;
                            return { ...s, current_week: Math.min(backendDevoData?.total_weeks || 52, curr + 1) };
                          })}
                          className={isStudio
                            ? 'w-7 h-7 rounded-lg border border-input bg-card hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors'
                            : 'w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 flex items-center justify-center text-slate-600 dark:text-slate-300 font-bold transition-colors'}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Group Selection */}
                    <div>
                      <label className={isStudio ? 'block text-[13px] font-medium text-foreground mb-2' : 'block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2'}>Who sees devo posts?</label>
                      {churchGroups.length === 0 ? (
                        <div className="p-3 bg-slate-50 dark:bg-[#1e1f22] rounded-lg border border-slate-200 dark:border-white/10">
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No groups yet.{' '}
                            <button 
                              onClick={() => { setShowDevoSettings(false); onOpenApp('comms', 'groups'); }}
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-200 font-medium"
                            >
                              Create your first group
                            </button>
                          </p>
                        </div>
                      ) : (
                        <div data-ui={isStudio ? 'devo-audience' : undefined} className="space-y-2">
                          <button
                            onClick={() => setDevoSettings(s => ({ ...s, group_ids: [] }))}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              devoSettings.group_ids.length === 0
                                ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900'
                                : 'bg-slate-50 dark:bg-[#1e1f22] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10'
                            }`}
                            data-testid="devo-all-groups"
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              devoSettings.group_ids.length === 0 ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 dark:border-white/15'
                            }`}>
                              {devoSettings.group_ids.length === 0 && <div className="w-2 h-2 rounded-full bg-card" />}
                            </div>
                            All groups (everyone)
                          </button>
                          {churchGroups.map(group => {
                            const isSelected = devoSettings.group_ids.includes(group.id || group._id);
                            return (
                              <button
                                key={group.id || group._id}
                                onClick={() => {
                                  const gid = group.id || group._id;
                                  setDevoSettings(s => ({
                                    ...s,
                                    group_ids: isSelected 
                                      ? s.group_ids.filter(id => id !== gid)
                                      : [...s.group_ids, gid]
                                  }));
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  isSelected
                                    ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900'
                                    : 'bg-slate-50 dark:bg-[#1e1f22] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10'
                                }`}
                                data-testid={`devo-group-${group.id || group._id}`}
                              >
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300 dark:border-white/15'
                                }`}>
                                  {isSelected && <div className="w-2 h-2 bg-card rounded-sm" />}
                                </div>
                                {group.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            
            <div className={isStudio
              ? 'px-5 pt-3 pb-4 flex justify-end gap-2 flex-shrink-0'
              : 'px-6 py-4 border-t border-slate-100 dark:border-white/10 flex justify-end gap-2 flex-shrink-0'}>
              <Button
                variant="ghost"
                className="rounded-lg text-sm"
                onClick={() => setShowDevoSettings(false)}
              >
                Cancel
              </Button>
              <Button
                className={isStudio ? 'rounded-lg text-sm' : 'bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm'}
                onClick={saveDevoSettings}
                data-testid="devo-settings-save"
              >
                {isStudio ? 'Save settings' : 'Save Settings'}
              </Button>
            </div>
        </StudioOrPlainModal>
      )}
      {servicesConnectGuard.dialog}
    </>
  );
}

export default Dashboard;
